import { typeMapper as a1 } from '../graphqlTypeMapper'
import { Observable } from 'graphql-typed-client'

export interface Query {
  currentUser: User | null
  loginMeta: LoginMeta | null
  getUser: User | null
  fund: Fund | null
  fundAggregation: FundAggregation | null
  fundAggregationQuarters: FundAggregation[]
  fundQuarters: Fund[]
  funds: Fund[]
  fundInvestment: FundInvestment | null
  fundInvestments: FundInvestment[]
  companyEv: CompanyEV
  fundScenario: FundScenario | null
  fundScenarios: (FundScenario | null)[] | null
  capTableScenario: CapTableScenario | null
  capTableScenarios: (CapTableScenario | null)[] | null
  company: Company | null
  companyByDocId: Company | null
  companies: Company[]
  nodeApiToken: String | null
  __typename: 'Query'
}

/**
 * The `Date` scalar type represents a Date
 * value as specified by
 * [iso8601](https://en.wikipedia.org/wiki/ISO_8601).
 */
export type Date = ReturnType<typeof a1.Date.deserialize>

/**
 * Leverages the internal Python implementation of UUID (uuid.UUID) to provide native UUID objects
 * in fields, resolvers and input.
 */
export type UUID = any

export interface User {
  id: UUID
  email: String
  firstName: String
  middleName: String
  lastName: String
  remoteId: String
  ventureCapital: VentureCapital | null
  profileLp: ProfileLimitedPartner | null
  deviceId: String
  role: Roles
  shortName: String
  lpInvestments: LPInvestment[]
  belongsToPlatform: Boolean | null
  getHelpUrl: String | null
  navigationPermissions: NavigationPermissions | null
  __typename: 'User'
}

/** The `String` scalar type represents textual data, represented as UTF-8 character sequences. The String type is most often used by GraphQL to represent free-form human-readable text. */
export type String = string

export interface VentureCapital {
  id: UUID
  name: String
  siteMode: VentureCapitalsVentureCapitalSiteModeChoices
  companies: Company[]
  __typename: 'VentureCapital'
}

/** An enumeration. */
export enum VentureCapitalsVentureCapitalSiteModeChoices {
  /** Default */
  A_10 = 'A_10',
  /** Lite */
  A_20 = 'A_20',
}

export interface Company {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  documentId: String
  documentVersionId: String
  documentsDump: String
  fdSharesAsInt: Boolean
  partner: String | null
  rawCurrency: CompaniesCompanyRawCurrencyChoices | null
  status: CompaniesCompanyStatusChoices
  statusDate: Date | null
  totalShares: Float | null
  useManualValuations: Boolean | null
  user: User
  name: String
  companyType: CompaniesCompanyCompanyTypeChoices
  ceoName: String
  contactName1: String
  contactName2: String
  phoneNumber: String
  email: String
  headquarters: String
  address1: String
  address2: String
  city: String
  state: String
  country: String
  postalCode: String
  url: String
  description: String
  foundingDate: Date | null
  blurb: String
  update: String
  sector: Sector | null
  exitDate: Date | null
  updatingUser: User | null
  updateDate: Date
  importSuccessStatus: Boolean
  entitiesCompanyQrId: String
  isAiCaptable: Boolean
  logo: String | null
  bgPhoto: String | null
  clonedVersions: ClonedVersion[] | null
  allFundInvestments: FundInvestment[]
  aggregationType: TagsTagTypeChoices | null
  aggregationId: UUID | null
  asOfDate: Date
  capTableScenarios: CapTableScenario[]
  currency: String
  dashboardSettings: Settings
  defaultDashboardSettings: Settings
  effectiveDate: DateTime | null
  fullAddress: String | null
  fundInvestments: FundInvestment[]
  inactivityDate: Date | null
  investedCapital: Float | null
  investmentDate: Date | null
  investmentsStatus: FundInvestmentsFundInvestmentStatusChoices | null
  investmentsStatusDisplay: String
  irr: Float | null
  ipoName: String | null
  jwtToken: String
  maxEv: Float | null
  minEv: Float | null
  multiple: Float | null
  nav: Float | null
  ownershipPercent: Float | null
  realizedValue: Float | null
  redeemedShares: Float | null
  sentiment: String | null
  sharePrice: Float | null
  topHolders: TopHolder[]
  totalProceeds: Float | null
  totalValue: Float | null
  __typename: 'Company'
}

/**
 * The `DateTime` scalar type represents a DateTime
 * value as specified by
 * [iso8601](https://en.wikipedia.org/wiki/ISO_8601).
 */
export type DateTime = any

/** The `Boolean` scalar type represents `true` or `false`. */
export type Boolean = boolean

/** An enumeration. */
export enum CompaniesCompanyRawCurrencyChoices {
  /** AUD */
  AUD = 'AUD',
  /** BRL */
  BRL = 'BRL',
  /** CAD */
  CAD = 'CAD',
  /** CHF */
  CHF = 'CHF',
  /** CNY */
  CNY = 'CNY',
  /** COP */
  COP = 'COP',
  /** DKK */
  DKK = 'DKK',
  /** EUR */
  EUR = 'EUR',
  /** GBP */
  GBP = 'GBP',
  /** IDR */
  IDR = 'IDR',
  /** ILS */
  ILS = 'ILS',
  /** INR */
  INR = 'INR',
  /** JPY */
  JPY = 'JPY',
  /** KRW */
  KRW = 'KRW',
  /** MMK */
  MMK = 'MMK',
  /** MXN */
  MXN = 'MXN',
  /** MYR */
  MYR = 'MYR',
  /** NOK */
  NOK = 'NOK',
  /** NZD */
  NZD = 'NZD',
  /** PHP */
  PHP = 'PHP',
  /** SEK */
  SEK = 'SEK',
  /** SGD */
  SGD = 'SGD',
  /** TRY */
  TRY = 'TRY',
  /** USD */
  USD = 'USD',
  /** VND */
  VND = 'VND',
  /** ZAR */
  ZAR = 'ZAR',
}

/** An enumeration. */
export enum CompaniesCompanyStatusChoices {
  /** Active */
  A_10 = 'A_10',
  /** Inactive */
  A_20 = 'A_20',
}

/** The `Float` scalar type represents signed double-precision fractional values as specified by [IEEE 754](https://en.wikipedia.org/wiki/IEEE_floating_point). */
export type Float = number

/** An enumeration. */
export enum CompaniesCompanyCompanyTypeChoices {
  /** C Corporation */
  CCORP = 'CCORP',
  /** LLC - Limited Liability Company */
  LLC = 'LLC',
}

export interface Sector {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  name: String
  multiple: Float | null
  companySet: Company[]
  __typename: 'Sector'
}

/** Data for the clones created from the Company's Captable. */
export interface ClonedVersion {
  clonedCapTableUrl: String
  clonedCapTableName: String
  __typename: 'ClonedVersion'
}

export interface FundInvestment {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  fund: Fund
  calculationStatus: FundInvestmentsFundInvestmentCalculationStatusChoices
  company: Company
  status: FundInvestmentsFundInvestmentStatusChoices
  investmentDate: Date | null
  inactivityDate: Date | null
  includeInScenariosCalcs: Boolean
  ipoName: String
  redeemedShares: Float | null
  escrow: Float | null
  escrowDate: Date | null
  comments: String
  sharePrice: Float | null
  sharePriceAud: Float | null
  sharePriceBrl: Float | null
  sharePriceCad: Float | null
  sharePriceChf: Float | null
  sharePriceCny: Float | null
  sharePriceCop: Float | null
  sharePriceDkk: Float | null
  sharePriceEur: Float | null
  sharePriceGbp: Float | null
  sharePriceIdr: Float | null
  sharePriceIls: Float | null
  sharePriceInr: Float | null
  sharePriceJpy: Float | null
  sharePriceKrw: Float | null
  sharePriceMmk: Float | null
  sharePriceMxn: Float | null
  sharePriceMyr: Float | null
  sharePriceNok: Float | null
  sharePriceNzd: Float | null
  sharePricePhp: Float | null
  sharePriceSek: Float | null
  sharePriceSgd: Float | null
  sharePriceTry: Float | null
  sharePriceUsd: Float | null
  sharePriceVnd: Float | null
  sharePriceZar: Float | null
  capTableUrl: String
  ev: Float
  investedCapital: Float
  investmentMemos: InvestmentMemo[]
  irr: Float | null
  multiple: Float
  nav: Float
  ownershipPercent: Float
  realizedValue: Float
  totalValue: Float
  totalProceeds: Float
  url: String
  statusDisplay: String
  scenarios: FundScenarioInvestmentScenario[]
  scenariosOwnFundAndParent: FundScenarioInvestmentScenario[]
  companyEvs: CompanyEV[]
  manualCompanyEv: CompanyEV | null
  __typename: 'FundInvestment'
}

export interface Fund {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  gp: User
  name: String
  description: String | null
  isInactive: Boolean
  inactivityDate: Date | null
  isSubfund: Boolean
  parentFund: Fund | null
  interestRate: Float
  managementFee: Float
  carriedInterest: Float
  closeDate: Date | null
  useManualIrr: Boolean
  apiFundId: String
  useExpensesInIrr: Boolean
  useExpensesInRvpi: Boolean
  lpinvestmentSet: LPInvestment[]
  subfunds: Fund[]
  fundscenarioSet: FundScenario[]
  asOfDate: Date
  bookValue: Float
  capitalCalled: Float
  companiesCount: Int
  rawCurrency: String
  currency: String
  currentLpInvestment: LPInvestment | null
  dashboardSettings: Settings
  defaultDashboardSettings: Settings
  dpi: Float
  divisions: Tag[]
  fundInvestments: FundInvestment[]
  grossIrr: Float | null
  grossMultiple: Float | null
  grossRealizedMultiple: Float | null
  groups: Tag[]
  investedCapital: Float
  investmentsBySector: ChartDataItem[]
  multiple: Float
  netTvpi: Float
  netIrr: Float | null
  realizedValue: Float
  rvpi: Float
  scenarios: (FundScenario | null)[]
  totalCommittedCapital: Float
  __typename: 'Fund'
}

export interface LPInvestment {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  lp: User
  fund: Fund
  committedDate: Date
  accruedInterest: Float
  note: String
  committedCapital: Float
  committedCapitalAud: Float | null
  committedCapitalBrl: Float | null
  committedCapitalCad: Float | null
  committedCapitalChf: Float | null
  committedCapitalCny: Float | null
  committedCapitalCop: Float | null
  committedCapitalDkk: Float | null
  committedCapitalEur: Float | null
  committedCapitalGbp: Float | null
  committedCapitalIdr: Float | null
  committedCapitalIls: Float | null
  committedCapitalInr: Float | null
  committedCapitalJpy: Float | null
  committedCapitalKrw: Float | null
  committedCapitalMmk: Float | null
  committedCapitalMxn: Float | null
  committedCapitalMyr: Float | null
  committedCapitalNok: Float | null
  committedCapitalNzd: Float | null
  committedCapitalPhp: Float | null
  committedCapitalSek: Float | null
  committedCapitalSgd: Float | null
  committedCapitalTry: Float | null
  committedCapitalUsd: Float | null
  committedCapitalVnd: Float | null
  committedCapitalZar: Float | null
  amount: Float
  bookValue: Float
  distributions: Float
  returnPercent: Float
  __typename: 'LPInvestment'
}

export interface FundScenario {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  defaultDilution: Float
  defaultExitDate: Date
  fund: Fund
  name: String
  position: Int
  companyevSet: CompanyEV[]
  investmentmemoSet: InvestmentMemo[]
  fundscenarioinvestmentscenarioSet: FundScenarioInvestmentScenario[]
  bookValue: Float | null
  calculationStatus: CalculationStatuses | null
  grossIrr: Float | null
  grossMultiple: Float | null
  investedCapital: Float | null
  __typename: 'FundScenario'
}

/** The `Int` scalar type represents non-fractional signed whole numeric values. Int can represent values between -(2^31) and 2^31 - 1. */
export type Int = number

export interface CompanyEV {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  asOfDate: Date
  fdShares: Float | null
  fundScenario: FundScenario | null
  investment: FundInvestment
  isCreatedManually: Boolean
  source: FundInvestmentsCompanyEVSourceChoices
  totalOwnership: Float | null
  type: FundInvestmentsCompanyEVTypeChoices
  useNav: Boolean
  rawCurrency: FundInvestmentsCompanyEVRawCurrencyChoices | null
  bookValue: Float
  ev: Float
  nav: Float
  sharePrice: Float | null
  warrantPrice: Float | null
  bookValueAud: Float | null
  bookValueBrl: Float | null
  bookValueCad: Float | null
  bookValueChf: Float | null
  bookValueCny: Float | null
  bookValueCop: Float | null
  bookValueDkk: Float | null
  bookValueEur: Float | null
  bookValueGbp: Float | null
  bookValueIdr: Float | null
  bookValueIls: Float | null
  bookValueInr: Float | null
  bookValueJpy: Float | null
  bookValueKrw: Float | null
  bookValueMmk: Float | null
  bookValueMxn: Float | null
  bookValueMyr: Float | null
  bookValueNok: Float | null
  bookValueNzd: Float | null
  bookValuePhp: Float | null
  bookValueSek: Float | null
  bookValueSgd: Float | null
  bookValueTry: Float | null
  bookValueUsd: Float | null
  bookValueVnd: Float | null
  bookValueZar: Float | null
  evAud: Float | null
  evBrl: Float | null
  evCad: Float | null
  evChf: Float | null
  evCny: Float | null
  evCop: Float | null
  evDkk: Float | null
  evEur: Float | null
  evGbp: Float | null
  evIdr: Float | null
  evIls: Float | null
  evInr: Float | null
  evJpy: Float | null
  evKrw: Float | null
  evMmk: Float | null
  evMxn: Float | null
  evMyr: Float | null
  evNok: Float | null
  evNzd: Float | null
  evPhp: Float | null
  evSek: Float | null
  evSgd: Float | null
  evTry: Float | null
  evUsd: Float | null
  evVnd: Float | null
  evZar: Float | null
  navAud: Float | null
  navBrl: Float | null
  navCad: Float | null
  navChf: Float | null
  navCny: Float | null
  navCop: Float | null
  navDkk: Float | null
  navEur: Float | null
  navGbp: Float | null
  navIdr: Float | null
  navIls: Float | null
  navInr: Float | null
  navJpy: Float | null
  navKrw: Float | null
  navMmk: Float | null
  navMxn: Float | null
  navMyr: Float | null
  navNok: Float | null
  navNzd: Float | null
  navPhp: Float | null
  navSek: Float | null
  navSgd: Float | null
  navTry: Float | null
  navUsd: Float | null
  navVnd: Float | null
  navZar: Float | null
  sharePriceAud: Float | null
  sharePriceBrl: Float | null
  sharePriceCad: Float | null
  sharePriceChf: Float | null
  sharePriceCny: Float | null
  sharePriceCop: Float | null
  sharePriceDkk: Float | null
  sharePriceEur: Float | null
  sharePriceGbp: Float | null
  sharePriceIdr: Float | null
  sharePriceIls: Float | null
  sharePriceInr: Float | null
  sharePriceJpy: Float | null
  sharePriceKrw: Float | null
  sharePriceMmk: Float | null
  sharePriceMxn: Float | null
  sharePriceMyr: Float | null
  sharePriceNok: Float | null
  sharePriceNzd: Float | null
  sharePricePhp: Float | null
  sharePriceSek: Float | null
  sharePriceSgd: Float | null
  sharePriceTry: Float | null
  sharePriceUsd: Float | null
  sharePriceVnd: Float | null
  sharePriceZar: Float | null
  warrantPriceAud: Float | null
  warrantPriceBrl: Float | null
  warrantPriceCad: Float | null
  warrantPriceChf: Float | null
  warrantPriceCny: Float | null
  warrantPriceCop: Float | null
  warrantPriceDkk: Float | null
  warrantPriceEur: Float | null
  warrantPriceGbp: Float | null
  warrantPriceIdr: Float | null
  warrantPriceIls: Float | null
  warrantPriceInr: Float | null
  warrantPriceJpy: Float | null
  warrantPriceKrw: Float | null
  warrantPriceMmk: Float | null
  warrantPriceMxn: Float | null
  warrantPriceMyr: Float | null
  warrantPriceNok: Float | null
  warrantPriceNzd: Float | null
  warrantPricePhp: Float | null
  warrantPriceSek: Float | null
  warrantPriceSgd: Float | null
  warrantPriceTry: Float | null
  warrantPriceUsd: Float | null
  warrantPriceVnd: Float | null
  warrantPriceZar: Float | null
  __typename: 'CompanyEV'
}

/** An enumeration. */
export enum FundInvestmentsCompanyEVSourceChoices {
  /** Manual Entry */
  A_10 = 'A_10',
  /** Excel Upload */
  A_20 = 'A_20',
  /** Cap Table */
  A_30 = 'A_30',
}

/** An enumeration. */
export enum FundInvestmentsCompanyEVTypeChoices {
  /** New PPS */
  A_10 = 'A_10',
  /** Shares Allocation */
  A_20 = 'A_20',
  /** Shares Conversion */
  A_30 = 'A_30',
  /** Repurchase */
  A_40 = 'A_40',
  /** New Plan */
  A_50 = 'A_50',
  /** Plan Modification */
  A_60 = 'A_60',
  /** Equity Cancellation */
  A_70 = 'A_70',
  /** Warrants Grant */
  A_80 = 'A_80',
  /** Warrants Cancellation */
  A_90 = 'A_90',
  /** Anti Dilution Shares (as converted) */
  A_100 = 'A_100',
  /** Notes Conversion */
  A_110 = 'A_110',
  /** Technical EV */
  A_120 = 'A_120',
}

/** An enumeration. */
export enum FundInvestmentsCompanyEVRawCurrencyChoices {
  /** AUD */
  AUD = 'AUD',
  /** BRL */
  BRL = 'BRL',
  /** CAD */
  CAD = 'CAD',
  /** CHF */
  CHF = 'CHF',
  /** CNY */
  CNY = 'CNY',
  /** COP */
  COP = 'COP',
  /** DKK */
  DKK = 'DKK',
  /** EUR */
  EUR = 'EUR',
  /** GBP */
  GBP = 'GBP',
  /** IDR */
  IDR = 'IDR',
  /** ILS */
  ILS = 'ILS',
  /** INR */
  INR = 'INR',
  /** JPY */
  JPY = 'JPY',
  /** KRW */
  KRW = 'KRW',
  /** MMK */
  MMK = 'MMK',
  /** MXN */
  MXN = 'MXN',
  /** MYR */
  MYR = 'MYR',
  /** NOK */
  NOK = 'NOK',
  /** NZD */
  NZD = 'NZD',
  /** PHP */
  PHP = 'PHP',
  /** SEK */
  SEK = 'SEK',
  /** SGD */
  SGD = 'SGD',
  /** TRY */
  TRY = 'TRY',
  /** USD */
  USD = 'USD',
  /** VND */
  VND = 'VND',
  /** ZAR */
  ZAR = 'ZAR',
}

export interface InvestmentMemo {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  advisoryInNav: Boolean | null
  advisoryShares: FundInvestmentsInvestmentMemoAdvisorySharesChoices | null
  advisorySharesAmount: Float | null
  advisorySharesType: FundInvestmentsInvestmentMemoAdvisorySharesTypeChoices | null
  antiDilution: String | null
  boardSeat: Int | null
  cnoteConversionDate: Date | null
  companyTotalShares: Float | null
  debtConvLanguage: String | null
  dragAlong: String | null
  employeePool: String | null
  equityDilution: Float | null
  fundParticipated: Boolean
  fundScenario: FundScenario | null
  informationRights: String | null
  interest: Float | null
  investment: FundInvestment
  irr: Float | null
  leadInvestor: String | null
  liquidationPreference: String | null
  notes: String | null
  payToPlay: Boolean
  participationRights: String | null
  preferenceLanguage: String | null
  proRata: String | null
  protectiveProvisions: String | null
  sharesPurchased: Float | null
  transactionDate: Date
  transactionName: String | null
  transactionType: FundInvestmentsInvestmentMemoTransactionTypeChoices
  warrants: Float | null
  warrantsInNav: Boolean | null
  rawCurrency: FundInvestmentsInvestmentMemoRawCurrencyChoices | null
  amount: Float
  companyTotalRaise: Float | null
  companyValuation: Float
  fmvPps: Float | null
  premoneyValuation: Float | null
  price: Float | null
  proceeds: Float
  totalRaised: Float | null
  valuationCap: Float | null
  warrantPrice: Float | null
  amountAud: Float | null
  amountBrl: Float | null
  amountCad: Float | null
  amountChf: Float | null
  amountCny: Float | null
  amountCop: Float | null
  amountDkk: Float | null
  amountEur: Float | null
  amountGbp: Float | null
  amountIdr: Float | null
  amountIls: Float | null
  amountInr: Float | null
  amountJpy: Float | null
  amountKrw: Float | null
  amountMmk: Float | null
  amountMxn: Float | null
  amountMyr: Float | null
  amountNok: Float | null
  amountNzd: Float | null
  amountPhp: Float | null
  amountSek: Float | null
  amountSgd: Float | null
  amountTry: Float | null
  amountUsd: Float | null
  amountVnd: Float | null
  amountZar: Float | null
  companyTotalRaiseAud: Float | null
  companyTotalRaiseBrl: Float | null
  companyTotalRaiseCad: Float | null
  companyTotalRaiseChf: Float | null
  companyTotalRaiseCny: Float | null
  companyTotalRaiseCop: Float | null
  companyTotalRaiseDkk: Float | null
  companyTotalRaiseEur: Float | null
  companyTotalRaiseGbp: Float | null
  companyTotalRaiseIdr: Float | null
  companyTotalRaiseIls: Float | null
  companyTotalRaiseInr: Float | null
  companyTotalRaiseJpy: Float | null
  companyTotalRaiseKrw: Float | null
  companyTotalRaiseMmk: Float | null
  companyTotalRaiseMxn: Float | null
  companyTotalRaiseMyr: Float | null
  companyTotalRaiseNok: Float | null
  companyTotalRaiseNzd: Float | null
  companyTotalRaisePhp: Float | null
  companyTotalRaiseSek: Float | null
  companyTotalRaiseSgd: Float | null
  companyTotalRaiseTry: Float | null
  companyTotalRaiseUsd: Float | null
  companyTotalRaiseVnd: Float | null
  companyTotalRaiseZar: Float | null
  companyValuationAud: Float | null
  companyValuationBrl: Float | null
  companyValuationCad: Float | null
  companyValuationChf: Float | null
  companyValuationCny: Float | null
  companyValuationCop: Float | null
  companyValuationDkk: Float | null
  companyValuationEur: Float | null
  companyValuationGbp: Float | null
  companyValuationIdr: Float | null
  companyValuationIls: Float | null
  companyValuationInr: Float | null
  companyValuationJpy: Float | null
  companyValuationKrw: Float | null
  companyValuationMmk: Float | null
  companyValuationMxn: Float | null
  companyValuationMyr: Float | null
  companyValuationNok: Float | null
  companyValuationNzd: Float | null
  companyValuationPhp: Float | null
  companyValuationSek: Float | null
  companyValuationSgd: Float | null
  companyValuationTry: Float | null
  companyValuationUsd: Float | null
  companyValuationVnd: Float | null
  companyValuationZar: Float | null
  fmvPpsAud: Float | null
  fmvPpsBrl: Float | null
  fmvPpsCad: Float | null
  fmvPpsChf: Float | null
  fmvPpsCny: Float | null
  fmvPpsCop: Float | null
  fmvPpsDkk: Float | null
  fmvPpsEur: Float | null
  fmvPpsGbp: Float | null
  fmvPpsIdr: Float | null
  fmvPpsIls: Float | null
  fmvPpsInr: Float | null
  fmvPpsJpy: Float | null
  fmvPpsKrw: Float | null
  fmvPpsMmk: Float | null
  fmvPpsMxn: Float | null
  fmvPpsMyr: Float | null
  fmvPpsNok: Float | null
  fmvPpsNzd: Float | null
  fmvPpsPhp: Float | null
  fmvPpsSek: Float | null
  fmvPpsSgd: Float | null
  fmvPpsTry: Float | null
  fmvPpsUsd: Float | null
  fmvPpsVnd: Float | null
  fmvPpsZar: Float | null
  premoneyValuationAud: Float | null
  premoneyValuationBrl: Float | null
  premoneyValuationCad: Float | null
  premoneyValuationChf: Float | null
  premoneyValuationCny: Float | null
  premoneyValuationCop: Float | null
  premoneyValuationDkk: Float | null
  premoneyValuationEur: Float | null
  premoneyValuationGbp: Float | null
  premoneyValuationIdr: Float | null
  premoneyValuationIls: Float | null
  premoneyValuationInr: Float | null
  premoneyValuationJpy: Float | null
  premoneyValuationKrw: Float | null
  premoneyValuationMmk: Float | null
  premoneyValuationMxn: Float | null
  premoneyValuationMyr: Float | null
  premoneyValuationNok: Float | null
  premoneyValuationNzd: Float | null
  premoneyValuationPhp: Float | null
  premoneyValuationSek: Float | null
  premoneyValuationSgd: Float | null
  premoneyValuationTry: Float | null
  premoneyValuationUsd: Float | null
  premoneyValuationVnd: Float | null
  premoneyValuationZar: Float | null
  priceAud: Float | null
  priceBrl: Float | null
  priceCad: Float | null
  priceChf: Float | null
  priceCny: Float | null
  priceCop: Float | null
  priceDkk: Float | null
  priceEur: Float | null
  priceGbp: Float | null
  priceIdr: Float | null
  priceIls: Float | null
  priceInr: Float | null
  priceJpy: Float | null
  priceKrw: Float | null
  priceMmk: Float | null
  priceMxn: Float | null
  priceMyr: Float | null
  priceNok: Float | null
  priceNzd: Float | null
  pricePhp: Float | null
  priceSek: Float | null
  priceSgd: Float | null
  priceTry: Float | null
  priceUsd: Float | null
  priceVnd: Float | null
  priceZar: Float | null
  proceedsAud: Float | null
  proceedsBrl: Float | null
  proceedsCad: Float | null
  proceedsChf: Float | null
  proceedsCny: Float | null
  proceedsCop: Float | null
  proceedsDkk: Float | null
  proceedsEur: Float | null
  proceedsGbp: Float | null
  proceedsIdr: Float | null
  proceedsIls: Float | null
  proceedsInr: Float | null
  proceedsJpy: Float | null
  proceedsKrw: Float | null
  proceedsMmk: Float | null
  proceedsMxn: Float | null
  proceedsMyr: Float | null
  proceedsNok: Float | null
  proceedsNzd: Float | null
  proceedsPhp: Float | null
  proceedsSek: Float | null
  proceedsSgd: Float | null
  proceedsTry: Float | null
  proceedsUsd: Float | null
  proceedsVnd: Float | null
  proceedsZar: Float | null
  totalRaisedAud: Float | null
  totalRaisedBrl: Float | null
  totalRaisedCad: Float | null
  totalRaisedChf: Float | null
  totalRaisedCny: Float | null
  totalRaisedCop: Float | null
  totalRaisedDkk: Float | null
  totalRaisedEur: Float | null
  totalRaisedGbp: Float | null
  totalRaisedIdr: Float | null
  totalRaisedIls: Float | null
  totalRaisedInr: Float | null
  totalRaisedJpy: Float | null
  totalRaisedKrw: Float | null
  totalRaisedMmk: Float | null
  totalRaisedMxn: Float | null
  totalRaisedMyr: Float | null
  totalRaisedNok: Float | null
  totalRaisedNzd: Float | null
  totalRaisedPhp: Float | null
  totalRaisedSek: Float | null
  totalRaisedSgd: Float | null
  totalRaisedTry: Float | null
  totalRaisedUsd: Float | null
  totalRaisedVnd: Float | null
  totalRaisedZar: Float | null
  valuationCapAud: Float | null
  valuationCapBrl: Float | null
  valuationCapCad: Float | null
  valuationCapChf: Float | null
  valuationCapCny: Float | null
  valuationCapCop: Float | null
  valuationCapDkk: Float | null
  valuationCapEur: Float | null
  valuationCapGbp: Float | null
  valuationCapIdr: Float | null
  valuationCapIls: Float | null
  valuationCapInr: Float | null
  valuationCapJpy: Float | null
  valuationCapKrw: Float | null
  valuationCapMmk: Float | null
  valuationCapMxn: Float | null
  valuationCapMyr: Float | null
  valuationCapNok: Float | null
  valuationCapNzd: Float | null
  valuationCapPhp: Float | null
  valuationCapSek: Float | null
  valuationCapSgd: Float | null
  valuationCapTry: Float | null
  valuationCapUsd: Float | null
  valuationCapVnd: Float | null
  valuationCapZar: Float | null
  warrantPriceAud: Float | null
  warrantPriceBrl: Float | null
  warrantPriceCad: Float | null
  warrantPriceChf: Float | null
  warrantPriceCny: Float | null
  warrantPriceCop: Float | null
  warrantPriceDkk: Float | null
  warrantPriceEur: Float | null
  warrantPriceGbp: Float | null
  warrantPriceIdr: Float | null
  warrantPriceIls: Float | null
  warrantPriceInr: Float | null
  warrantPriceJpy: Float | null
  warrantPriceKrw: Float | null
  warrantPriceMmk: Float | null
  warrantPriceMxn: Float | null
  warrantPriceMyr: Float | null
  warrantPriceNok: Float | null
  warrantPriceNzd: Float | null
  warrantPricePhp: Float | null
  warrantPriceSek: Float | null
  warrantPriceSgd: Float | null
  warrantPriceTry: Float | null
  warrantPriceUsd: Float | null
  warrantPriceVnd: Float | null
  warrantPriceZar: Float | null
  multiple: Float | null
  __typename: 'InvestmentMemo'
}

/** An enumeration. */
export enum FundInvestmentsInvestmentMemoAdvisorySharesChoices {
  /** No Shares */
  A_10 = 'A_10',
  /** Shares */
  A_20 = 'A_20',
  /** Warrants */
  A_30 = 'A_30',
}

/** An enumeration. */
export enum FundInvestmentsInvestmentMemoAdvisorySharesTypeChoices {
  /** Amount of shares */
  A_10 = 'A_10',
  /** Percentage of total shares */
  A_20 = 'A_20',
}

/** An enumeration. */
export enum FundInvestmentsInvestmentMemoTransactionTypeChoices {
  /** Non-converted Note */
  A_10 = 'A_10',
  /** Equity */
  A_20 = 'A_20',
  /** Common */
  A_21 = 'A_21',
  /** Preferred */
  A_22 = 'A_22',
  /** Sales */
  A_23 = 'A_23',
  /** Converted Notes */
  A_30 = 'A_30',
  /** Warrant */
  A_40 = 'A_40',
}

/** An enumeration. */
export enum FundInvestmentsInvestmentMemoRawCurrencyChoices {
  /** AUD */
  AUD = 'AUD',
  /** BRL */
  BRL = 'BRL',
  /** CAD */
  CAD = 'CAD',
  /** CHF */
  CHF = 'CHF',
  /** CNY */
  CNY = 'CNY',
  /** COP */
  COP = 'COP',
  /** DKK */
  DKK = 'DKK',
  /** EUR */
  EUR = 'EUR',
  /** GBP */
  GBP = 'GBP',
  /** IDR */
  IDR = 'IDR',
  /** ILS */
  ILS = 'ILS',
  /** INR */
  INR = 'INR',
  /** JPY */
  JPY = 'JPY',
  /** KRW */
  KRW = 'KRW',
  /** MMK */
  MMK = 'MMK',
  /** MXN */
  MXN = 'MXN',
  /** MYR */
  MYR = 'MYR',
  /** NOK */
  NOK = 'NOK',
  /** NZD */
  NZD = 'NZD',
  /** PHP */
  PHP = 'PHP',
  /** SEK */
  SEK = 'SEK',
  /** SGD */
  SGD = 'SGD',
  /** TRY */
  TRY = 'TRY',
  /** USD */
  USD = 'USD',
  /** VND */
  VND = 'VND',
  /** ZAR */
  ZAR = 'ZAR',
}

export interface FundScenarioInvestmentScenario {
  id: UUID | null
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  capTableScenario: CapTableScenario | null
  exitAmount: Float
  exitDate: Date | null
  dilution: Float | null
  fundScenario: FundScenario
  nav: Float | null
  irr: Float | null
  multiple: Float | null
  position: Int | null
  __typename: 'FundScenarioInvestmentScenario'
}

export interface CapTableScenario {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  company: Company | null
  name: String
  fundscenarioinvestmentscenarioSet: FundScenarioInvestmentScenario[]
  __typename: 'CapTableScenario'
}

export enum CalculationStatuses {
  NEW = 'NEW',
  CALCULATED = 'CALCULATED',
  IN_PROGRESS = 'IN_PROGRESS',
  ERROR = 'ERROR',
}

export interface Settings {
  dashboardType: Types
  sections: Section[]
  __typename: 'Settings'
}

export enum Types {
  /** FUND */
  FUND = 'FUND',
  /** COMPANY */
  COMPANY = 'COMPANY',
}

export interface Section {
  name: String
  order: Int
  checked: Boolean
  components: Component[]
  __typename: 'Section'
}

export interface Component {
  name: String
  order: Int
  checked: Boolean
  __typename: 'Component'
}

export interface Tag {
  id: UUID
  type: TagsTagTypeChoices | null
  name: String
  __typename: 'Tag'
}

/** An enumeration. */
export enum TagsTagTypeChoices {
  /** Fund division */
  FUND_DIVISION = 'FUND_DIVISION',
  /** Fund group */
  FUND_GROUP = 'FUND_GROUP',
}

export interface ChartDataItem {
  name: String
  y: Float
  __typename: 'ChartDataItem'
}

/** An enumeration. */
export enum FundInvestmentsFundInvestmentCalculationStatusChoices {
  /** New */
  A_0 = 'A_0',
  /** Calculated */
  A_10 = 'A_10',
  /** In Progress */
  A_20 = 'A_20',
  /** Error On Invest */
  A_30 = 'A_30',
  /** Error On Datasync */
  A_40 = 'A_40',
}

/** An enumeration. */
export enum FundInvestmentsFundInvestmentStatusChoices {
  /** Active */
  A_10 = 'A_10',
  /** Inactive - Written Off */
  A_20 = 'A_20',
  /** Inactive - Exit */
  A_30 = 'A_30',
  /** Inactive - Full Position Sale */
  A_40 = 'A_40',
  /** Inactive - Other */
  A_50 = 'A_50',
}

export interface TopHolder {
  color: Int
  holdingsAmount: Float
  holdingsPercent: Float
  name: String
  __typename: 'TopHolder'
}

export interface ProfileLimitedPartner {
  id: UUID
  dateCreated: DateTime
  dateUpdated: DateTime
  ventureCapital: VentureCapital
  status: UsersProfileLimitedPartnerStatusChoices
  user: User
  gp: User | null
  baseCurrency: UsersProfileLimitedPartnerBaseCurrencyChoices
  legalEntity: String
  identifier: String
  address1: String
  address2: String
  city: String
  state: String
  postalCode: String
  phoneNumber1: String
  phoneNumber2: String
  prefContactName: String
  prefContactTitle: String
  prefContactEmail: String
  prefContactPhone: String
  bankName: String
  bankAbaNumber: String
  bankAccountNumber: String
  additionalInstructions: String
  accruedPreference: Float
  committedCapital: Float
  contributedCapital: Float
  distributedCapital: Float
  nav: Float
  returnPercent: Float | null
  __typename: 'ProfileLimitedPartner'
}

/** An enumeration. */
export enum UsersProfileLimitedPartnerStatusChoices {
  /** Active */
  A_10 = 'A_10',
  /** Inactive */
  A_20 = 'A_20',
}

/** An enumeration. */
export enum UsersProfileLimitedPartnerBaseCurrencyChoices {
  /** AUD */
  AUD = 'AUD',
  /** BRL */
  BRL = 'BRL',
  /** CAD */
  CAD = 'CAD',
  /** CHF */
  CHF = 'CHF',
  /** CNY */
  CNY = 'CNY',
  /** COP */
  COP = 'COP',
  /** DKK */
  DKK = 'DKK',
  /** EUR */
  EUR = 'EUR',
  /** GBP */
  GBP = 'GBP',
  /** IDR */
  IDR = 'IDR',
  /** ILS */
  ILS = 'ILS',
  /** INR */
  INR = 'INR',
  /** JPY */
  JPY = 'JPY',
  /** KRW */
  KRW = 'KRW',
  /** MMK */
  MMK = 'MMK',
  /** MXN */
  MXN = 'MXN',
  /** MYR */
  MYR = 'MYR',
  /** NOK */
  NOK = 'NOK',
  /** NZD */
  NZD = 'NZD',
  /** PHP */
  PHP = 'PHP',
  /** SEK */
  SEK = 'SEK',
  /** SGD */
  SGD = 'SGD',
  /** TRY */
  TRY = 'TRY',
  /** USD */
  USD = 'USD',
  /** VND */
  VND = 'VND',
  /** ZAR */
  ZAR = 'ZAR',
}

export enum Roles {
  /** GP */
  GP = 'GP',
  /** LP */
  LP = 'LP',
  /** CO */
  CO = 'CO',
  /** PM */
  PM = 'PM',
  /** CV */
  CV = 'CV',
}

/**
 * Used to denote which navigation tabs to be displayed on the front-end.
 * Also used by the backend, if incase backend authorization is needed.
 *
 * Note: This is configured on the `User` model.
 */
export interface NavigationPermissions {
  isSiteModeDefault: Boolean | null
  isSiteModeLite: Boolean | null
  isLimitedPartner: Boolean | null
  isGpOrPm: Boolean | null
  isCompanyPageAccessible: Boolean | null
  __typename: 'NavigationPermissions'
}

/** Type declaration for User's login meta information. */
export interface LoginMeta {
  oidcLoginUrl: String | null
  ssoLoginUrl: String | null
  nonPlatformLoginExists: Boolean | null
  platformLoginExists: Boolean | null
  __typename: 'LoginMeta'
}

export interface FundAggregation {
  aggregationType: TagsTagTypeChoices | null
  aggregationId: UUID | null
  asOfDate: Date
  bookValue: Float
  capitalCalled: Float
  companiesCount: Int
  currency: String
  currentLpInvestment: LPInvestment | null
  dashboardSettings: Settings
  defaultDashboardSettings: Settings
  dpi: Float | null
  funds: Fund[]
  grossIrr: Float | null
  grossMultiple: Float | null
  grossRealizedMultiple: Float | null
  investedCapital: Float
  investmentsBySector: ChartDataItem[]
  isSingleFund: Boolean
  multiple: Float
  name: String
  netTvpi: Float | null
  netIrr: Float | null
  realizedValue: Float
  rvpi: Float | null
  totalCommittedCapital: Float
  clonedCaptables: ClonedCaptable[] | null
  __typename: 'FundAggregation'
}

/** Data for the clones of companies under the Fund. */
export interface ClonedCaptable {
  clonedCapTableUrl: String
  clonedCapTableName: String
  clonedParentName: String
  __typename: 'ClonedCaptable'
}

export interface Mutation {
  sandBoxRestore: Boolean | null
  login: Login | null
  passwordChange: PasswordChange | null
  fundDashboardSettingsUpdate: DashboardSettingsUpdate | null
  fundInvestmentChange: (FundInvestment | null)[] | null
  capTableReportGenerate: CapTableReportGenerate | null
  capTableScenariosUpdate: (CapTableScenario | null)[] | null
  fundScenariosAdd: (FundScenario | null)[] | null
  fundScenariosChange: (FundScenario | null)[] | null
  /** Triggers datasync to calculate FundScenarios  */
  fundScenariosCalculate: Boolean | null
  /** Deletes scenarios by list of IDs and returns amount of deleted rows.  */
  fundScenariosDelete: Int | null
  fundScenarioInvestmentScenariosAdd: (FundScenarioInvestmentScenario | null)[] | null
  fundScenarioInvestmentScenariosChange: (FundScenarioInvestmentScenario | null)[] | null
  /** Deletes scenarios by list of IDs and returns amount of deleted rows.  */
  fundScenarioInvestmentScenariosDelete: Int | null
  companyDashboardSettingsUpdate: DashboardSettingsUpdate | null
  __typename: 'Mutation'
}

export interface Login {
  session: String | null
  error: ErrorsEnumLogin | null
  __typename: 'Login'
}

export enum ErrorsEnumLogin {
  WRONG_CREDS = 'WRONG_CREDS',
  INACTIVE = 'INACTIVE',
  PASSWORD_EXPIRED = 'PASSWORD_EXPIRED',
  RATE_LIMITED = 'RATE_LIMITED',
}

export interface PasswordChange {
  session: String | null
  errorCode: ErrorsEnumPasswordChange | null
  additionalMessages: (String | null)[] | null
  __typename: 'PasswordChange'
}

export enum ErrorsEnumPasswordChange {
  WRONG_CREDS = 'WRONG_CREDS',
  INACTIVE = 'INACTIVE',
  SAME_PASSWORD = 'SAME_PASSWORD',
  PASSWORDS_MISMATCH = 'PASSWORDS_MISMATCH',
  INVALID_PASSWORD = 'INVALID_PASSWORD',
  UNABLE_TO_AUTH = 'UNABLE_TO_AUTH',
}

export interface DashboardSettingsUpdate {
  dashboardSettings: Settings
  __typename: 'DashboardSettingsUpdate'
}

export interface CapTableReportGenerate {
  success: Boolean | null
  __typename: 'CapTableReportGenerate'
}

/**
 * Represents a string that is safe to be used in Excel.
 *
 * Used to filter out:
 *     - excel formula injection
 *     - CSV injection
 */
export type ExcelSafeString = any

export interface QueryRequest {
  currentUser?: [{ asOfDate?: Date | null; fundId?: UUID | null }, UserRequest] | UserRequest
  loginMeta?: [{ email?: String | null }, LoginMetaRequest] | LoginMetaRequest
  getUser?: [{ asOfDate?: Date | null; remoteId: String }, UserRequest]
  fund?: [{ id?: UUID | null; asOfDate?: Date | null }, FundRequest] | FundRequest
  fundAggregation?:
    | [
        {
          aggregationId?: UUID | null
          aggregationType?: TagsTagTypeChoices | null
          currency?: String | null
          asOfDate?: Date | null
        },
        FundAggregationRequest,
      ]
    | FundAggregationRequest
  fundAggregationQuarters?:
    | [
        {
          aggregationId?: UUID | null
          aggregationType?: TagsTagTypeChoices | null
          currency?: String | null
          endDate?: Date | null
        },
        FundAggregationRequest,
      ]
    | FundAggregationRequest
  fundQuarters?: [{ id: UUID; endDate?: Date | null }, FundRequest]
  funds?: FundRequest
  fundInvestment?: [{ id?: UUID | null; asOfDate?: Date | null }, FundInvestmentRequest] | FundInvestmentRequest
  fundInvestments?:
    | [{ fundId?: UUID | null; includeSubfunds?: Boolean | null }, FundInvestmentRequest]
    | FundInvestmentRequest
  companyEv?: [{ id: UUID }, CompanyEVRequest]
  fundScenario?: [{ id: UUID }, FundScenarioRequest]
  fundScenarios?: [{ fundId: UUID }, FundScenarioRequest]
  capTableScenario?: [{ name: String }, CapTableScenarioRequest]
  capTableScenarios?: CapTableScenarioRequest
  company?:
    | [
        {
          id?: UUID | null
          asOfDate?: Date | null
          aggregationType?: TagsTagTypeChoices | null
          aggregationId?: UUID | null
          currency?: String | null
        },
        CompanyRequest,
      ]
    | CompanyRequest
  companyByDocId?: [{ documentId: String }, CompanyRequest]
  companies?: CompanyRequest
  nodeApiToken?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface UserRequest {
  id?: boolean | number
  email?: boolean | number
  firstName?: boolean | number
  middleName?: boolean | number
  lastName?: boolean | number
  remoteId?: boolean | number
  ventureCapital?: VentureCapitalRequest
  profileLp?: ProfileLimitedPartnerRequest
  deviceId?: boolean | number
  role?: boolean | number
  shortName?: boolean | number
  lpInvestments?: LPInvestmentRequest
  belongsToPlatform?: boolean | number
  getHelpUrl?: boolean | number
  navigationPermissions?: NavigationPermissionsRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface VentureCapitalRequest {
  id?: boolean | number
  name?: boolean | number
  siteMode?: boolean | number
  companies?: CompanyRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CompanyRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  documentId?: boolean | number
  documentVersionId?: boolean | number
  documentsDump?: boolean | number
  fdSharesAsInt?: boolean | number
  partner?: boolean | number
  rawCurrency?: boolean | number
  status?: boolean | number
  statusDate?: boolean | number
  totalShares?: boolean | number
  useManualValuations?: boolean | number
  user?: UserRequest
  name?: boolean | number
  companyType?: boolean | number
  ceoName?: boolean | number
  contactName1?: boolean | number
  contactName2?: boolean | number
  phoneNumber?: boolean | number
  email?: boolean | number
  headquarters?: boolean | number
  address1?: boolean | number
  address2?: boolean | number
  city?: boolean | number
  state?: boolean | number
  country?: boolean | number
  postalCode?: boolean | number
  url?: boolean | number
  description?: boolean | number
  foundingDate?: boolean | number
  blurb?: boolean | number
  update?: boolean | number
  sector?: SectorRequest
  exitDate?: boolean | number
  updatingUser?: UserRequest
  updateDate?: boolean | number
  importSuccessStatus?: boolean | number
  entitiesCompanyQrId?: boolean | number
  isAiCaptable?: boolean | number
  logo?: boolean | number
  bgPhoto?: boolean | number
  clonedVersions?: ClonedVersionRequest
  allFundInvestments?: FundInvestmentRequest
  aggregationType?: boolean | number
  aggregationId?: boolean | number
  asOfDate?: boolean | number
  capTableScenarios?: CapTableScenarioRequest
  currency?: boolean | number
  dashboardSettings?: SettingsRequest
  defaultDashboardSettings?: SettingsRequest
  effectiveDate?: boolean | number
  fullAddress?: boolean | number
  fundInvestments?: FundInvestmentRequest
  inactivityDate?: boolean | number
  investedCapital?: boolean | number
  investmentDate?: boolean | number
  investmentsStatus?: boolean | number
  investmentsStatusDisplay?: boolean | number
  irr?: boolean | number
  ipoName?: boolean | number
  jwtToken?: boolean | number
  maxEv?: boolean | number
  minEv?: boolean | number
  multiple?: boolean | number
  nav?: boolean | number
  ownershipPercent?: boolean | number
  realizedValue?: boolean | number
  redeemedShares?: boolean | number
  sentiment?: boolean | number
  sharePrice?: boolean | number
  topHolders?: TopHolderRequest
  totalProceeds?: boolean | number
  totalValue?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface SectorRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  name?: boolean | number
  multiple?: boolean | number
  companySet?: CompanyRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

/** Data for the clones created from the Company's Captable. */
export interface ClonedVersionRequest {
  clonedCapTableUrl?: boolean | number
  clonedCapTableName?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface FundInvestmentRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  fund?: FundRequest
  calculationStatus?: boolean | number
  company?: CompanyRequest
  status?: boolean | number
  investmentDate?: boolean | number
  inactivityDate?: boolean | number
  includeInScenariosCalcs?: boolean | number
  ipoName?: boolean | number
  redeemedShares?: boolean | number
  escrow?: boolean | number
  escrowDate?: boolean | number
  comments?: boolean | number
  sharePrice?: boolean | number
  sharePriceAud?: boolean | number
  sharePriceBrl?: boolean | number
  sharePriceCad?: boolean | number
  sharePriceChf?: boolean | number
  sharePriceCny?: boolean | number
  sharePriceCop?: boolean | number
  sharePriceDkk?: boolean | number
  sharePriceEur?: boolean | number
  sharePriceGbp?: boolean | number
  sharePriceIdr?: boolean | number
  sharePriceIls?: boolean | number
  sharePriceInr?: boolean | number
  sharePriceJpy?: boolean | number
  sharePriceKrw?: boolean | number
  sharePriceMmk?: boolean | number
  sharePriceMxn?: boolean | number
  sharePriceMyr?: boolean | number
  sharePriceNok?: boolean | number
  sharePriceNzd?: boolean | number
  sharePricePhp?: boolean | number
  sharePriceSek?: boolean | number
  sharePriceSgd?: boolean | number
  sharePriceTry?: boolean | number
  sharePriceUsd?: boolean | number
  sharePriceVnd?: boolean | number
  sharePriceZar?: boolean | number
  capTableUrl?: boolean | number
  ev?: boolean | number
  investedCapital?: boolean | number
  investmentMemos?: InvestmentMemoRequest
  irr?: boolean | number
  multiple?: boolean | number
  nav?: boolean | number
  ownershipPercent?: boolean | number
  realizedValue?: boolean | number
  totalValue?: boolean | number
  totalProceeds?: boolean | number
  url?: boolean | number
  statusDisplay?: boolean | number
  scenarios?: FundScenarioInvestmentScenarioRequest
  scenariosOwnFundAndParent?: FundScenarioInvestmentScenarioRequest
  companyEvs?: CompanyEVRequest
  manualCompanyEv?: CompanyEVRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface FundRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  gp?: UserRequest
  name?: boolean | number
  description?: boolean | number
  isInactive?: boolean | number
  inactivityDate?: boolean | number
  isSubfund?: boolean | number
  parentFund?: FundRequest
  interestRate?: boolean | number
  managementFee?: boolean | number
  carriedInterest?: boolean | number
  closeDate?: boolean | number
  useManualIrr?: boolean | number
  apiFundId?: boolean | number
  useExpensesInIrr?: boolean | number
  useExpensesInRvpi?: boolean | number
  lpinvestmentSet?: LPInvestmentRequest
  subfunds?: FundRequest
  fundscenarioSet?: FundScenarioRequest
  asOfDate?: boolean | number
  bookValue?: boolean | number
  capitalCalled?: boolean | number
  companiesCount?: boolean | number
  rawCurrency?: boolean | number
  currency?: boolean | number
  currentLpInvestment?: LPInvestmentRequest
  dashboardSettings?: SettingsRequest
  defaultDashboardSettings?: SettingsRequest
  dpi?: boolean | number
  divisions?: TagRequest
  fundInvestments?: FundInvestmentRequest
  grossIrr?: boolean | number
  grossMultiple?: boolean | number
  grossRealizedMultiple?: boolean | number
  groups?: TagRequest
  investedCapital?: boolean | number
  investmentsBySector?: ChartDataItemRequest
  multiple?: boolean | number
  netTvpi?: boolean | number
  netIrr?: boolean | number
  realizedValue?: boolean | number
  rvpi?: boolean | number
  scenarios?: FundScenarioRequest
  totalCommittedCapital?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface LPInvestmentRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  lp?: UserRequest
  fund?: FundRequest
  committedDate?: boolean | number
  accruedInterest?: boolean | number
  note?: boolean | number
  committedCapital?: boolean | number
  committedCapitalAud?: boolean | number
  committedCapitalBrl?: boolean | number
  committedCapitalCad?: boolean | number
  committedCapitalChf?: boolean | number
  committedCapitalCny?: boolean | number
  committedCapitalCop?: boolean | number
  committedCapitalDkk?: boolean | number
  committedCapitalEur?: boolean | number
  committedCapitalGbp?: boolean | number
  committedCapitalIdr?: boolean | number
  committedCapitalIls?: boolean | number
  committedCapitalInr?: boolean | number
  committedCapitalJpy?: boolean | number
  committedCapitalKrw?: boolean | number
  committedCapitalMmk?: boolean | number
  committedCapitalMxn?: boolean | number
  committedCapitalMyr?: boolean | number
  committedCapitalNok?: boolean | number
  committedCapitalNzd?: boolean | number
  committedCapitalPhp?: boolean | number
  committedCapitalSek?: boolean | number
  committedCapitalSgd?: boolean | number
  committedCapitalTry?: boolean | number
  committedCapitalUsd?: boolean | number
  committedCapitalVnd?: boolean | number
  committedCapitalZar?: boolean | number
  amount?: boolean | number
  bookValue?: boolean | number
  distributions?: boolean | number
  returnPercent?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface FundScenarioRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  defaultDilution?: boolean | number
  defaultExitDate?: boolean | number
  fund?: FundRequest
  name?: boolean | number
  position?: boolean | number
  companyevSet?: CompanyEVRequest
  investmentmemoSet?: InvestmentMemoRequest
  fundscenarioinvestmentscenarioSet?: FundScenarioInvestmentScenarioRequest
  bookValue?: boolean | number
  calculationStatus?: boolean | number
  grossIrr?: boolean | number
  grossMultiple?: boolean | number
  investedCapital?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CompanyEVRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  asOfDate?: boolean | number
  fdShares?: boolean | number
  fundScenario?: FundScenarioRequest
  investment?: FundInvestmentRequest
  isCreatedManually?: boolean | number
  source?: boolean | number
  totalOwnership?: boolean | number
  type?: boolean | number
  useNav?: boolean | number
  rawCurrency?: boolean | number
  bookValue?: boolean | number
  ev?: boolean | number
  nav?: boolean | number
  sharePrice?: boolean | number
  warrantPrice?: boolean | number
  bookValueAud?: boolean | number
  bookValueBrl?: boolean | number
  bookValueCad?: boolean | number
  bookValueChf?: boolean | number
  bookValueCny?: boolean | number
  bookValueCop?: boolean | number
  bookValueDkk?: boolean | number
  bookValueEur?: boolean | number
  bookValueGbp?: boolean | number
  bookValueIdr?: boolean | number
  bookValueIls?: boolean | number
  bookValueInr?: boolean | number
  bookValueJpy?: boolean | number
  bookValueKrw?: boolean | number
  bookValueMmk?: boolean | number
  bookValueMxn?: boolean | number
  bookValueMyr?: boolean | number
  bookValueNok?: boolean | number
  bookValueNzd?: boolean | number
  bookValuePhp?: boolean | number
  bookValueSek?: boolean | number
  bookValueSgd?: boolean | number
  bookValueTry?: boolean | number
  bookValueUsd?: boolean | number
  bookValueVnd?: boolean | number
  bookValueZar?: boolean | number
  evAud?: boolean | number
  evBrl?: boolean | number
  evCad?: boolean | number
  evChf?: boolean | number
  evCny?: boolean | number
  evCop?: boolean | number
  evDkk?: boolean | number
  evEur?: boolean | number
  evGbp?: boolean | number
  evIdr?: boolean | number
  evIls?: boolean | number
  evInr?: boolean | number
  evJpy?: boolean | number
  evKrw?: boolean | number
  evMmk?: boolean | number
  evMxn?: boolean | number
  evMyr?: boolean | number
  evNok?: boolean | number
  evNzd?: boolean | number
  evPhp?: boolean | number
  evSek?: boolean | number
  evSgd?: boolean | number
  evTry?: boolean | number
  evUsd?: boolean | number
  evVnd?: boolean | number
  evZar?: boolean | number
  navAud?: boolean | number
  navBrl?: boolean | number
  navCad?: boolean | number
  navChf?: boolean | number
  navCny?: boolean | number
  navCop?: boolean | number
  navDkk?: boolean | number
  navEur?: boolean | number
  navGbp?: boolean | number
  navIdr?: boolean | number
  navIls?: boolean | number
  navInr?: boolean | number
  navJpy?: boolean | number
  navKrw?: boolean | number
  navMmk?: boolean | number
  navMxn?: boolean | number
  navMyr?: boolean | number
  navNok?: boolean | number
  navNzd?: boolean | number
  navPhp?: boolean | number
  navSek?: boolean | number
  navSgd?: boolean | number
  navTry?: boolean | number
  navUsd?: boolean | number
  navVnd?: boolean | number
  navZar?: boolean | number
  sharePriceAud?: boolean | number
  sharePriceBrl?: boolean | number
  sharePriceCad?: boolean | number
  sharePriceChf?: boolean | number
  sharePriceCny?: boolean | number
  sharePriceCop?: boolean | number
  sharePriceDkk?: boolean | number
  sharePriceEur?: boolean | number
  sharePriceGbp?: boolean | number
  sharePriceIdr?: boolean | number
  sharePriceIls?: boolean | number
  sharePriceInr?: boolean | number
  sharePriceJpy?: boolean | number
  sharePriceKrw?: boolean | number
  sharePriceMmk?: boolean | number
  sharePriceMxn?: boolean | number
  sharePriceMyr?: boolean | number
  sharePriceNok?: boolean | number
  sharePriceNzd?: boolean | number
  sharePricePhp?: boolean | number
  sharePriceSek?: boolean | number
  sharePriceSgd?: boolean | number
  sharePriceTry?: boolean | number
  sharePriceUsd?: boolean | number
  sharePriceVnd?: boolean | number
  sharePriceZar?: boolean | number
  warrantPriceAud?: boolean | number
  warrantPriceBrl?: boolean | number
  warrantPriceCad?: boolean | number
  warrantPriceChf?: boolean | number
  warrantPriceCny?: boolean | number
  warrantPriceCop?: boolean | number
  warrantPriceDkk?: boolean | number
  warrantPriceEur?: boolean | number
  warrantPriceGbp?: boolean | number
  warrantPriceIdr?: boolean | number
  warrantPriceIls?: boolean | number
  warrantPriceInr?: boolean | number
  warrantPriceJpy?: boolean | number
  warrantPriceKrw?: boolean | number
  warrantPriceMmk?: boolean | number
  warrantPriceMxn?: boolean | number
  warrantPriceMyr?: boolean | number
  warrantPriceNok?: boolean | number
  warrantPriceNzd?: boolean | number
  warrantPricePhp?: boolean | number
  warrantPriceSek?: boolean | number
  warrantPriceSgd?: boolean | number
  warrantPriceTry?: boolean | number
  warrantPriceUsd?: boolean | number
  warrantPriceVnd?: boolean | number
  warrantPriceZar?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface InvestmentMemoRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  advisoryInNav?: boolean | number
  advisoryShares?: boolean | number
  advisorySharesAmount?: boolean | number
  advisorySharesType?: boolean | number
  antiDilution?: boolean | number
  boardSeat?: boolean | number
  cnoteConversionDate?: boolean | number
  companyTotalShares?: boolean | number
  debtConvLanguage?: boolean | number
  dragAlong?: boolean | number
  employeePool?: boolean | number
  equityDilution?: boolean | number
  fundParticipated?: boolean | number
  fundScenario?: FundScenarioRequest
  informationRights?: boolean | number
  interest?: boolean | number
  investment?: FundInvestmentRequest
  irr?: boolean | number
  leadInvestor?: boolean | number
  liquidationPreference?: boolean | number
  notes?: boolean | number
  payToPlay?: boolean | number
  participationRights?: boolean | number
  preferenceLanguage?: boolean | number
  proRata?: boolean | number
  protectiveProvisions?: boolean | number
  sharesPurchased?: boolean | number
  transactionDate?: boolean | number
  transactionName?: boolean | number
  transactionType?: boolean | number
  warrants?: boolean | number
  warrantsInNav?: boolean | number
  rawCurrency?: boolean | number
  amount?: boolean | number
  companyTotalRaise?: boolean | number
  companyValuation?: boolean | number
  fmvPps?: boolean | number
  premoneyValuation?: boolean | number
  price?: boolean | number
  proceeds?: boolean | number
  totalRaised?: boolean | number
  valuationCap?: boolean | number
  warrantPrice?: boolean | number
  amountAud?: boolean | number
  amountBrl?: boolean | number
  amountCad?: boolean | number
  amountChf?: boolean | number
  amountCny?: boolean | number
  amountCop?: boolean | number
  amountDkk?: boolean | number
  amountEur?: boolean | number
  amountGbp?: boolean | number
  amountIdr?: boolean | number
  amountIls?: boolean | number
  amountInr?: boolean | number
  amountJpy?: boolean | number
  amountKrw?: boolean | number
  amountMmk?: boolean | number
  amountMxn?: boolean | number
  amountMyr?: boolean | number
  amountNok?: boolean | number
  amountNzd?: boolean | number
  amountPhp?: boolean | number
  amountSek?: boolean | number
  amountSgd?: boolean | number
  amountTry?: boolean | number
  amountUsd?: boolean | number
  amountVnd?: boolean | number
  amountZar?: boolean | number
  companyTotalRaiseAud?: boolean | number
  companyTotalRaiseBrl?: boolean | number
  companyTotalRaiseCad?: boolean | number
  companyTotalRaiseChf?: boolean | number
  companyTotalRaiseCny?: boolean | number
  companyTotalRaiseCop?: boolean | number
  companyTotalRaiseDkk?: boolean | number
  companyTotalRaiseEur?: boolean | number
  companyTotalRaiseGbp?: boolean | number
  companyTotalRaiseIdr?: boolean | number
  companyTotalRaiseIls?: boolean | number
  companyTotalRaiseInr?: boolean | number
  companyTotalRaiseJpy?: boolean | number
  companyTotalRaiseKrw?: boolean | number
  companyTotalRaiseMmk?: boolean | number
  companyTotalRaiseMxn?: boolean | number
  companyTotalRaiseMyr?: boolean | number
  companyTotalRaiseNok?: boolean | number
  companyTotalRaiseNzd?: boolean | number
  companyTotalRaisePhp?: boolean | number
  companyTotalRaiseSek?: boolean | number
  companyTotalRaiseSgd?: boolean | number
  companyTotalRaiseTry?: boolean | number
  companyTotalRaiseUsd?: boolean | number
  companyTotalRaiseVnd?: boolean | number
  companyTotalRaiseZar?: boolean | number
  companyValuationAud?: boolean | number
  companyValuationBrl?: boolean | number
  companyValuationCad?: boolean | number
  companyValuationChf?: boolean | number
  companyValuationCny?: boolean | number
  companyValuationCop?: boolean | number
  companyValuationDkk?: boolean | number
  companyValuationEur?: boolean | number
  companyValuationGbp?: boolean | number
  companyValuationIdr?: boolean | number
  companyValuationIls?: boolean | number
  companyValuationInr?: boolean | number
  companyValuationJpy?: boolean | number
  companyValuationKrw?: boolean | number
  companyValuationMmk?: boolean | number
  companyValuationMxn?: boolean | number
  companyValuationMyr?: boolean | number
  companyValuationNok?: boolean | number
  companyValuationNzd?: boolean | number
  companyValuationPhp?: boolean | number
  companyValuationSek?: boolean | number
  companyValuationSgd?: boolean | number
  companyValuationTry?: boolean | number
  companyValuationUsd?: boolean | number
  companyValuationVnd?: boolean | number
  companyValuationZar?: boolean | number
  fmvPpsAud?: boolean | number
  fmvPpsBrl?: boolean | number
  fmvPpsCad?: boolean | number
  fmvPpsChf?: boolean | number
  fmvPpsCny?: boolean | number
  fmvPpsCop?: boolean | number
  fmvPpsDkk?: boolean | number
  fmvPpsEur?: boolean | number
  fmvPpsGbp?: boolean | number
  fmvPpsIdr?: boolean | number
  fmvPpsIls?: boolean | number
  fmvPpsInr?: boolean | number
  fmvPpsJpy?: boolean | number
  fmvPpsKrw?: boolean | number
  fmvPpsMmk?: boolean | number
  fmvPpsMxn?: boolean | number
  fmvPpsMyr?: boolean | number
  fmvPpsNok?: boolean | number
  fmvPpsNzd?: boolean | number
  fmvPpsPhp?: boolean | number
  fmvPpsSek?: boolean | number
  fmvPpsSgd?: boolean | number
  fmvPpsTry?: boolean | number
  fmvPpsUsd?: boolean | number
  fmvPpsVnd?: boolean | number
  fmvPpsZar?: boolean | number
  premoneyValuationAud?: boolean | number
  premoneyValuationBrl?: boolean | number
  premoneyValuationCad?: boolean | number
  premoneyValuationChf?: boolean | number
  premoneyValuationCny?: boolean | number
  premoneyValuationCop?: boolean | number
  premoneyValuationDkk?: boolean | number
  premoneyValuationEur?: boolean | number
  premoneyValuationGbp?: boolean | number
  premoneyValuationIdr?: boolean | number
  premoneyValuationIls?: boolean | number
  premoneyValuationInr?: boolean | number
  premoneyValuationJpy?: boolean | number
  premoneyValuationKrw?: boolean | number
  premoneyValuationMmk?: boolean | number
  premoneyValuationMxn?: boolean | number
  premoneyValuationMyr?: boolean | number
  premoneyValuationNok?: boolean | number
  premoneyValuationNzd?: boolean | number
  premoneyValuationPhp?: boolean | number
  premoneyValuationSek?: boolean | number
  premoneyValuationSgd?: boolean | number
  premoneyValuationTry?: boolean | number
  premoneyValuationUsd?: boolean | number
  premoneyValuationVnd?: boolean | number
  premoneyValuationZar?: boolean | number
  priceAud?: boolean | number
  priceBrl?: boolean | number
  priceCad?: boolean | number
  priceChf?: boolean | number
  priceCny?: boolean | number
  priceCop?: boolean | number
  priceDkk?: boolean | number
  priceEur?: boolean | number
  priceGbp?: boolean | number
  priceIdr?: boolean | number
  priceIls?: boolean | number
  priceInr?: boolean | number
  priceJpy?: boolean | number
  priceKrw?: boolean | number
  priceMmk?: boolean | number
  priceMxn?: boolean | number
  priceMyr?: boolean | number
  priceNok?: boolean | number
  priceNzd?: boolean | number
  pricePhp?: boolean | number
  priceSek?: boolean | number
  priceSgd?: boolean | number
  priceTry?: boolean | number
  priceUsd?: boolean | number
  priceVnd?: boolean | number
  priceZar?: boolean | number
  proceedsAud?: boolean | number
  proceedsBrl?: boolean | number
  proceedsCad?: boolean | number
  proceedsChf?: boolean | number
  proceedsCny?: boolean | number
  proceedsCop?: boolean | number
  proceedsDkk?: boolean | number
  proceedsEur?: boolean | number
  proceedsGbp?: boolean | number
  proceedsIdr?: boolean | number
  proceedsIls?: boolean | number
  proceedsInr?: boolean | number
  proceedsJpy?: boolean | number
  proceedsKrw?: boolean | number
  proceedsMmk?: boolean | number
  proceedsMxn?: boolean | number
  proceedsMyr?: boolean | number
  proceedsNok?: boolean | number
  proceedsNzd?: boolean | number
  proceedsPhp?: boolean | number
  proceedsSek?: boolean | number
  proceedsSgd?: boolean | number
  proceedsTry?: boolean | number
  proceedsUsd?: boolean | number
  proceedsVnd?: boolean | number
  proceedsZar?: boolean | number
  totalRaisedAud?: boolean | number
  totalRaisedBrl?: boolean | number
  totalRaisedCad?: boolean | number
  totalRaisedChf?: boolean | number
  totalRaisedCny?: boolean | number
  totalRaisedCop?: boolean | number
  totalRaisedDkk?: boolean | number
  totalRaisedEur?: boolean | number
  totalRaisedGbp?: boolean | number
  totalRaisedIdr?: boolean | number
  totalRaisedIls?: boolean | number
  totalRaisedInr?: boolean | number
  totalRaisedJpy?: boolean | number
  totalRaisedKrw?: boolean | number
  totalRaisedMmk?: boolean | number
  totalRaisedMxn?: boolean | number
  totalRaisedMyr?: boolean | number
  totalRaisedNok?: boolean | number
  totalRaisedNzd?: boolean | number
  totalRaisedPhp?: boolean | number
  totalRaisedSek?: boolean | number
  totalRaisedSgd?: boolean | number
  totalRaisedTry?: boolean | number
  totalRaisedUsd?: boolean | number
  totalRaisedVnd?: boolean | number
  totalRaisedZar?: boolean | number
  valuationCapAud?: boolean | number
  valuationCapBrl?: boolean | number
  valuationCapCad?: boolean | number
  valuationCapChf?: boolean | number
  valuationCapCny?: boolean | number
  valuationCapCop?: boolean | number
  valuationCapDkk?: boolean | number
  valuationCapEur?: boolean | number
  valuationCapGbp?: boolean | number
  valuationCapIdr?: boolean | number
  valuationCapIls?: boolean | number
  valuationCapInr?: boolean | number
  valuationCapJpy?: boolean | number
  valuationCapKrw?: boolean | number
  valuationCapMmk?: boolean | number
  valuationCapMxn?: boolean | number
  valuationCapMyr?: boolean | number
  valuationCapNok?: boolean | number
  valuationCapNzd?: boolean | number
  valuationCapPhp?: boolean | number
  valuationCapSek?: boolean | number
  valuationCapSgd?: boolean | number
  valuationCapTry?: boolean | number
  valuationCapUsd?: boolean | number
  valuationCapVnd?: boolean | number
  valuationCapZar?: boolean | number
  warrantPriceAud?: boolean | number
  warrantPriceBrl?: boolean | number
  warrantPriceCad?: boolean | number
  warrantPriceChf?: boolean | number
  warrantPriceCny?: boolean | number
  warrantPriceCop?: boolean | number
  warrantPriceDkk?: boolean | number
  warrantPriceEur?: boolean | number
  warrantPriceGbp?: boolean | number
  warrantPriceIdr?: boolean | number
  warrantPriceIls?: boolean | number
  warrantPriceInr?: boolean | number
  warrantPriceJpy?: boolean | number
  warrantPriceKrw?: boolean | number
  warrantPriceMmk?: boolean | number
  warrantPriceMxn?: boolean | number
  warrantPriceMyr?: boolean | number
  warrantPriceNok?: boolean | number
  warrantPriceNzd?: boolean | number
  warrantPricePhp?: boolean | number
  warrantPriceSek?: boolean | number
  warrantPriceSgd?: boolean | number
  warrantPriceTry?: boolean | number
  warrantPriceUsd?: boolean | number
  warrantPriceVnd?: boolean | number
  warrantPriceZar?: boolean | number
  multiple?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface FundScenarioInvestmentScenarioRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  capTableScenario?: CapTableScenarioRequest
  exitAmount?: boolean | number
  exitDate?: boolean | number
  dilution?: boolean | number
  fundScenario?: FundScenarioRequest
  nav?: boolean | number
  irr?: boolean | number
  multiple?: boolean | number
  position?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CapTableScenarioRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  company?: CompanyRequest
  name?: boolean | number
  fundscenarioinvestmentscenarioSet?: FundScenarioInvestmentScenarioRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface SettingsRequest {
  dashboardType?: boolean | number
  sections?: SectionRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface SectionRequest {
  name?: boolean | number
  order?: boolean | number
  checked?: boolean | number
  components?: ComponentRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ComponentRequest {
  name?: boolean | number
  order?: boolean | number
  checked?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface TagRequest {
  id?: boolean | number
  type?: boolean | number
  name?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ChartDataItemRequest {
  name?: boolean | number
  y?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface TopHolderRequest {
  color?: boolean | number
  holdingsAmount?: boolean | number
  holdingsPercent?: boolean | number
  name?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface ProfileLimitedPartnerRequest {
  id?: boolean | number
  dateCreated?: boolean | number
  dateUpdated?: boolean | number
  ventureCapital?: VentureCapitalRequest
  status?: boolean | number
  user?: UserRequest
  gp?: UserRequest
  baseCurrency?: boolean | number
  legalEntity?: boolean | number
  identifier?: boolean | number
  address1?: boolean | number
  address2?: boolean | number
  city?: boolean | number
  state?: boolean | number
  postalCode?: boolean | number
  phoneNumber1?: boolean | number
  phoneNumber2?: boolean | number
  prefContactName?: boolean | number
  prefContactTitle?: boolean | number
  prefContactEmail?: boolean | number
  prefContactPhone?: boolean | number
  bankName?: boolean | number
  bankAbaNumber?: boolean | number
  bankAccountNumber?: boolean | number
  additionalInstructions?: boolean | number
  accruedPreference?: boolean | number
  committedCapital?: boolean | number
  contributedCapital?: boolean | number
  distributedCapital?: boolean | number
  nav?: boolean | number
  returnPercent?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

/**
 * Used to denote which navigation tabs to be displayed on the front-end.
 * Also used by the backend, if incase backend authorization is needed.
 *
 * Note: This is configured on the `User` model.
 */
export interface NavigationPermissionsRequest {
  isSiteModeDefault?: boolean | number
  isSiteModeLite?: boolean | number
  isLimitedPartner?: boolean | number
  isGpOrPm?: boolean | number
  isCompanyPageAccessible?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

/** Type declaration for User's login meta information. */
export interface LoginMetaRequest {
  oidcLoginUrl?: boolean | number
  ssoLoginUrl?: boolean | number
  nonPlatformLoginExists?: boolean | number
  platformLoginExists?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface FundAggregationRequest {
  aggregationType?: boolean | number
  aggregationId?: boolean | number
  asOfDate?: boolean | number
  bookValue?: boolean | number
  capitalCalled?: boolean | number
  companiesCount?: boolean | number
  currency?: boolean | number
  currentLpInvestment?: LPInvestmentRequest
  dashboardSettings?: SettingsRequest
  defaultDashboardSettings?: SettingsRequest
  dpi?: boolean | number
  funds?: FundRequest
  grossIrr?: boolean | number
  grossMultiple?: boolean | number
  grossRealizedMultiple?: boolean | number
  investedCapital?: boolean | number
  investmentsBySector?: ChartDataItemRequest
  isSingleFund?: boolean | number
  multiple?: boolean | number
  name?: boolean | number
  netTvpi?: boolean | number
  netIrr?: boolean | number
  realizedValue?: boolean | number
  rvpi?: boolean | number
  totalCommittedCapital?: boolean | number
  clonedCaptables?: ClonedCaptableRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

/** Data for the clones of companies under the Fund. */
export interface ClonedCaptableRequest {
  clonedCapTableUrl?: boolean | number
  clonedCapTableName?: boolean | number
  clonedParentName?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface MutationRequest {
  sandBoxRestore?: [{ sourceId: UUID; targetId: UUID }]
  login?: [{ email: String; password: String }, LoginRequest]
  passwordChange?: [{ email: String; oldPassword: String; password1: String; password2: String }, PasswordChangeRequest]
  fundDashboardSettingsUpdate?: [{ dashboardSettings: SectionInput[] }, DashboardSettingsUpdateRequest]
  fundInvestmentChange?: [{ fundInvestments: (FundInvestmentChangeItem | null)[] }, FundInvestmentRequest]
  capTableReportGenerate?: [{ asOfDate: Date; email: String; investmentId: UUID }, CapTableReportGenerateRequest]
  capTableScenariosUpdate?: [{ scenarios: (CapTableScenarioItem | null)[] }, CapTableScenarioRequest]
  fundScenariosAdd?: [{ scenarioSets: (FundScenariosAddSet | null)[] }, FundScenarioRequest]
  fundScenariosChange?: [{ scenarios: (FundScenariosChangeItem | null)[] }, FundScenarioRequest]
  /** Triggers datasync to calculate FundScenarios  */
  fundScenariosCalculate?: [{ fundId: UUID; investmentId?: UUID | null }]
  /** Deletes scenarios by list of IDs and returns amount of deleted rows.  */
  fundScenariosDelete?: [{ scenarios: (UUID | null)[] }]
  fundScenarioInvestmentScenariosAdd?: [
    { scenarioSets: (FundScenarioInvestmentScenarioAddSet | null)[] },
    FundScenarioInvestmentScenarioRequest,
  ]
  fundScenarioInvestmentScenariosChange?: [
    { scenarios: (FundScenarioInvestmentScenariosChangeItem | null)[] },
    FundScenarioInvestmentScenarioRequest,
  ]
  /** Deletes scenarios by list of IDs and returns amount of deleted rows.  */
  fundScenarioInvestmentScenariosDelete?: [{ scenarios: (UUID | null)[] }]
  companyDashboardSettingsUpdate?: [{ companyId: UUID; dashboardSettings: SectionInput[] }, DashboardSettingsUpdateRequest]
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface LoginRequest {
  session?: boolean | number
  error?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface PasswordChangeRequest {
  session?: boolean | number
  errorCode?: boolean | number
  additionalMessages?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface SectionInput {
  name: String
  order?: Int | null
  checked?: Boolean | null
  components?: (ComponentInput | null)[] | null
}

export interface ComponentInput {
  name: String
  order?: Int | null
  checked?: Boolean | null
}

export interface DashboardSettingsUpdateRequest {
  dashboardSettings?: SettingsRequest
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface FundInvestmentChangeItem {
  includeInScenariosCalcs: Boolean
  id: UUID
}

export interface CapTableReportGenerateRequest {
  success?: boolean | number
  __typename?: boolean | number
  __scalar?: boolean | number
}

export interface CapTableScenarioItem {
  name: String
}

export interface FundScenariosAddSet {
  fundId: UUID
  scenarios: (FundScenariosAddItem | null)[]
}

export interface FundScenariosAddItem {
  defaultDilution: Float
  defaultExitDate: Date
  name: ExcelSafeString
  position: Int
}

export interface FundScenariosChangeItem {
  defaultDilution: Float
  defaultExitDate: Date
  name: ExcelSafeString
  position: Int
  id: UUID
}

export interface FundScenarioInvestmentScenarioAddSet {
  fundInvestmentId: UUID
  scenarios: (FundScenarioInvestmentScenarioAddItem | null)[]
}

export interface FundScenarioInvestmentScenarioAddItem {
  capTableScenarioId?: UUID | null
  exitAmount: Float
  exitDate?: Date | null
  dilution?: Float | null
  fundScenarioId: UUID
}

export interface FundScenarioInvestmentScenariosChangeItem {
  capTableScenarioId?: UUID | null
  exitAmount: Float
  exitDate?: Date | null
  dilution?: Float | null
  id: UUID
}

const Query_possibleTypes = ['Query']
export const isQuery = (obj: { __typename: String }): obj is Query => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Query_possibleTypes.includes(obj.__typename)
}

const User_possibleTypes = ['User']
export const isUser = (obj: { __typename: String }): obj is User => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return User_possibleTypes.includes(obj.__typename)
}

const VentureCapital_possibleTypes = ['VentureCapital']
export const isVentureCapital = (obj: { __typename: String }): obj is VentureCapital => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return VentureCapital_possibleTypes.includes(obj.__typename)
}

const Company_possibleTypes = ['Company']
export const isCompany = (obj: { __typename: String }): obj is Company => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Company_possibleTypes.includes(obj.__typename)
}

const Sector_possibleTypes = ['Sector']
export const isSector = (obj: { __typename: String }): obj is Sector => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Sector_possibleTypes.includes(obj.__typename)
}

const ClonedVersion_possibleTypes = ['ClonedVersion']
export const isClonedVersion = (obj: { __typename: String }): obj is ClonedVersion => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ClonedVersion_possibleTypes.includes(obj.__typename)
}

const FundInvestment_possibleTypes = ['FundInvestment']
export const isFundInvestment = (obj: { __typename: String }): obj is FundInvestment => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return FundInvestment_possibleTypes.includes(obj.__typename)
}

const Fund_possibleTypes = ['Fund']
export const isFund = (obj: { __typename: String }): obj is Fund => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Fund_possibleTypes.includes(obj.__typename)
}

const LPInvestment_possibleTypes = ['LPInvestment']
export const isLPInvestment = (obj: { __typename: String }): obj is LPInvestment => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return LPInvestment_possibleTypes.includes(obj.__typename)
}

const FundScenario_possibleTypes = ['FundScenario']
export const isFundScenario = (obj: { __typename: String }): obj is FundScenario => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return FundScenario_possibleTypes.includes(obj.__typename)
}

const CompanyEV_possibleTypes = ['CompanyEV']
export const isCompanyEV = (obj: { __typename: String }): obj is CompanyEV => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CompanyEV_possibleTypes.includes(obj.__typename)
}

const InvestmentMemo_possibleTypes = ['InvestmentMemo']
export const isInvestmentMemo = (obj: { __typename: String }): obj is InvestmentMemo => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return InvestmentMemo_possibleTypes.includes(obj.__typename)
}

const FundScenarioInvestmentScenario_possibleTypes = ['FundScenarioInvestmentScenario']
export const isFundScenarioInvestmentScenario = (obj: { __typename: String }): obj is FundScenarioInvestmentScenario => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return FundScenarioInvestmentScenario_possibleTypes.includes(obj.__typename)
}

const CapTableScenario_possibleTypes = ['CapTableScenario']
export const isCapTableScenario = (obj: { __typename: String }): obj is CapTableScenario => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CapTableScenario_possibleTypes.includes(obj.__typename)
}

const Settings_possibleTypes = ['Settings']
export const isSettings = (obj: { __typename: String }): obj is Settings => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Settings_possibleTypes.includes(obj.__typename)
}

const Section_possibleTypes = ['Section']
export const isSection = (obj: { __typename: String }): obj is Section => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Section_possibleTypes.includes(obj.__typename)
}

const Component_possibleTypes = ['Component']
export const isComponent = (obj: { __typename: String }): obj is Component => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Component_possibleTypes.includes(obj.__typename)
}

const Tag_possibleTypes = ['Tag']
export const isTag = (obj: { __typename: String }): obj is Tag => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Tag_possibleTypes.includes(obj.__typename)
}

const ChartDataItem_possibleTypes = ['ChartDataItem']
export const isChartDataItem = (obj: { __typename: String }): obj is ChartDataItem => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ChartDataItem_possibleTypes.includes(obj.__typename)
}

const TopHolder_possibleTypes = ['TopHolder']
export const isTopHolder = (obj: { __typename: String }): obj is TopHolder => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return TopHolder_possibleTypes.includes(obj.__typename)
}

const ProfileLimitedPartner_possibleTypes = ['ProfileLimitedPartner']
export const isProfileLimitedPartner = (obj: { __typename: String }): obj is ProfileLimitedPartner => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ProfileLimitedPartner_possibleTypes.includes(obj.__typename)
}

const NavigationPermissions_possibleTypes = ['NavigationPermissions']
export const isNavigationPermissions = (obj: { __typename: String }): obj is NavigationPermissions => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return NavigationPermissions_possibleTypes.includes(obj.__typename)
}

const LoginMeta_possibleTypes = ['LoginMeta']
export const isLoginMeta = (obj: { __typename: String }): obj is LoginMeta => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return LoginMeta_possibleTypes.includes(obj.__typename)
}

const FundAggregation_possibleTypes = ['FundAggregation']
export const isFundAggregation = (obj: { __typename: String }): obj is FundAggregation => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return FundAggregation_possibleTypes.includes(obj.__typename)
}

const ClonedCaptable_possibleTypes = ['ClonedCaptable']
export const isClonedCaptable = (obj: { __typename: String }): obj is ClonedCaptable => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return ClonedCaptable_possibleTypes.includes(obj.__typename)
}

const Mutation_possibleTypes = ['Mutation']
export const isMutation = (obj: { __typename: String }): obj is Mutation => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Mutation_possibleTypes.includes(obj.__typename)
}

const Login_possibleTypes = ['Login']
export const isLogin = (obj: { __typename: String }): obj is Login => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return Login_possibleTypes.includes(obj.__typename)
}

const PasswordChange_possibleTypes = ['PasswordChange']
export const isPasswordChange = (obj: { __typename: String }): obj is PasswordChange => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return PasswordChange_possibleTypes.includes(obj.__typename)
}

const DashboardSettingsUpdate_possibleTypes = ['DashboardSettingsUpdate']
export const isDashboardSettingsUpdate = (obj: { __typename: String }): obj is DashboardSettingsUpdate => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return DashboardSettingsUpdate_possibleTypes.includes(obj.__typename)
}

const CapTableReportGenerate_possibleTypes = ['CapTableReportGenerate']
export const isCapTableReportGenerate = (obj: { __typename: String }): obj is CapTableReportGenerate => {
  if (!obj.__typename) throw new Error('__typename is missing')
  return CapTableReportGenerate_possibleTypes.includes(obj.__typename)
}

export interface QueryPromiseChain {
  currentUser: ((args?: {
    asOfDate?: Date | null
    fundId?: UUID | null
  }) => UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User | null) => Promise<User | null> }) &
    (UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User | null) => Promise<User | null> })
  loginMeta: ((args?: {
    email?: String | null
  }) => LoginMetaPromiseChain & {
    execute: (request: LoginMetaRequest, defaultValue?: LoginMeta | null) => Promise<LoginMeta | null>
  }) &
    (LoginMetaPromiseChain & {
      execute: (request: LoginMetaRequest, defaultValue?: LoginMeta | null) => Promise<LoginMeta | null>
    })
  getUser: (args: {
    asOfDate?: Date | null
    remoteId: String
  }) => UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User | null) => Promise<User | null> }
  fund: ((args?: {
    id?: UUID | null
    asOfDate?: Date | null
  }) => FundPromiseChain & { execute: (request: FundRequest, defaultValue?: Fund | null) => Promise<Fund | null> }) &
    (FundPromiseChain & { execute: (request: FundRequest, defaultValue?: Fund | null) => Promise<Fund | null> })
  fundAggregation: ((args?: {
    aggregationId?: UUID | null
    aggregationType?: TagsTagTypeChoices | null
    currency?: String | null
    asOfDate?: Date | null
  }) => FundAggregationPromiseChain & {
    execute: (request: FundAggregationRequest, defaultValue?: FundAggregation | null) => Promise<FundAggregation | null>
  }) &
    (FundAggregationPromiseChain & {
      execute: (request: FundAggregationRequest, defaultValue?: FundAggregation | null) => Promise<FundAggregation | null>
    })
  fundAggregationQuarters: ((args?: {
    aggregationId?: UUID | null
    aggregationType?: TagsTagTypeChoices | null
    currency?: String | null
    endDate?: Date | null
  }) => { execute: (request: FundAggregationRequest, defaultValue?: FundAggregation[]) => Promise<FundAggregation[]> }) & {
    execute: (request: FundAggregationRequest, defaultValue?: FundAggregation[]) => Promise<FundAggregation[]>
  }
  fundQuarters: (args: {
    id: UUID
    endDate?: Date | null
  }) => { execute: (request: FundRequest, defaultValue?: Fund[]) => Promise<Fund[]> }
  funds: { execute: (request: FundRequest, defaultValue?: Fund[]) => Promise<Fund[]> }
  fundInvestment: ((args?: {
    id?: UUID | null
    asOfDate?: Date | null
  }) => FundInvestmentPromiseChain & {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment | null) => Promise<FundInvestment | null>
  }) &
    (FundInvestmentPromiseChain & {
      execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment | null) => Promise<FundInvestment | null>
    })
  fundInvestments: ((args?: {
    fundId?: UUID | null
    includeSubfunds?: Boolean | null
  }) => { execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Promise<FundInvestment[]> }) & {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Promise<FundInvestment[]>
  }
  companyEv: (args: {
    id: UUID
  }) => CompanyEVPromiseChain & { execute: (request: CompanyEVRequest, defaultValue?: CompanyEV) => Promise<CompanyEV> }
  fundScenario: (args: {
    id: UUID
  }) => FundScenarioPromiseChain & {
    execute: (request: FundScenarioRequest, defaultValue?: FundScenario | null) => Promise<FundScenario | null>
  }
  fundScenarios: (args: {
    fundId: UUID
  }) => {
    execute: (
      request: FundScenarioRequest,
      defaultValue?: (FundScenario | null)[] | null,
    ) => Promise<(FundScenario | null)[] | null>
  }
  capTableScenario: (args: {
    name: String
  }) => CapTableScenarioPromiseChain & {
    execute: (request: CapTableScenarioRequest, defaultValue?: CapTableScenario | null) => Promise<CapTableScenario | null>
  }
  capTableScenarios: {
    execute: (
      request: CapTableScenarioRequest,
      defaultValue?: (CapTableScenario | null)[] | null,
    ) => Promise<(CapTableScenario | null)[] | null>
  }
  company: ((args?: {
    id?: UUID | null
    asOfDate?: Date | null
    aggregationType?: TagsTagTypeChoices | null
    aggregationId?: UUID | null
    currency?: String | null
  }) => CompanyPromiseChain & {
    execute: (request: CompanyRequest, defaultValue?: Company | null) => Promise<Company | null>
  }) &
    (CompanyPromiseChain & { execute: (request: CompanyRequest, defaultValue?: Company | null) => Promise<Company | null> })
  companyByDocId: (args: {
    documentId: String
  }) => CompanyPromiseChain & {
    execute: (request: CompanyRequest, defaultValue?: Company | null) => Promise<Company | null>
  }
  companies: { execute: (request: CompanyRequest, defaultValue?: Company[]) => Promise<Company[]> }
  nodeApiToken: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
}

export interface QueryObservableChain {
  currentUser: ((args?: {
    asOfDate?: Date | null
    fundId?: UUID | null
  }) => UserObservableChain & { execute: (request: UserRequest, defaultValue?: User | null) => Observable<User | null> }) &
    (UserObservableChain & { execute: (request: UserRequest, defaultValue?: User | null) => Observable<User | null> })
  loginMeta: ((args?: {
    email?: String | null
  }) => LoginMetaObservableChain & {
    execute: (request: LoginMetaRequest, defaultValue?: LoginMeta | null) => Observable<LoginMeta | null>
  }) &
    (LoginMetaObservableChain & {
      execute: (request: LoginMetaRequest, defaultValue?: LoginMeta | null) => Observable<LoginMeta | null>
    })
  getUser: (args: {
    asOfDate?: Date | null
    remoteId: String
  }) => UserObservableChain & { execute: (request: UserRequest, defaultValue?: User | null) => Observable<User | null> }
  fund: ((args?: {
    id?: UUID | null
    asOfDate?: Date | null
  }) => FundObservableChain & { execute: (request: FundRequest, defaultValue?: Fund | null) => Observable<Fund | null> }) &
    (FundObservableChain & { execute: (request: FundRequest, defaultValue?: Fund | null) => Observable<Fund | null> })
  fundAggregation: ((args?: {
    aggregationId?: UUID | null
    aggregationType?: TagsTagTypeChoices | null
    currency?: String | null
    asOfDate?: Date | null
  }) => FundAggregationObservableChain & {
    execute: (request: FundAggregationRequest, defaultValue?: FundAggregation | null) => Observable<FundAggregation | null>
  }) &
    (FundAggregationObservableChain & {
      execute: (request: FundAggregationRequest, defaultValue?: FundAggregation | null) => Observable<FundAggregation | null>
    })
  fundAggregationQuarters: ((args?: {
    aggregationId?: UUID | null
    aggregationType?: TagsTagTypeChoices | null
    currency?: String | null
    endDate?: Date | null
  }) => {
    execute: (request: FundAggregationRequest, defaultValue?: FundAggregation[]) => Observable<FundAggregation[]>
  }) & { execute: (request: FundAggregationRequest, defaultValue?: FundAggregation[]) => Observable<FundAggregation[]> }
  fundQuarters: (args: {
    id: UUID
    endDate?: Date | null
  }) => { execute: (request: FundRequest, defaultValue?: Fund[]) => Observable<Fund[]> }
  funds: { execute: (request: FundRequest, defaultValue?: Fund[]) => Observable<Fund[]> }
  fundInvestment: ((args?: {
    id?: UUID | null
    asOfDate?: Date | null
  }) => FundInvestmentObservableChain & {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment | null) => Observable<FundInvestment | null>
  }) &
    (FundInvestmentObservableChain & {
      execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment | null) => Observable<FundInvestment | null>
    })
  fundInvestments: ((args?: {
    fundId?: UUID | null
    includeSubfunds?: Boolean | null
  }) => { execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Observable<FundInvestment[]> }) & {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Observable<FundInvestment[]>
  }
  companyEv: (args: {
    id: UUID
  }) => CompanyEVObservableChain & {
    execute: (request: CompanyEVRequest, defaultValue?: CompanyEV) => Observable<CompanyEV>
  }
  fundScenario: (args: {
    id: UUID
  }) => FundScenarioObservableChain & {
    execute: (request: FundScenarioRequest, defaultValue?: FundScenario | null) => Observable<FundScenario | null>
  }
  fundScenarios: (args: {
    fundId: UUID
  }) => {
    execute: (
      request: FundScenarioRequest,
      defaultValue?: (FundScenario | null)[] | null,
    ) => Observable<(FundScenario | null)[] | null>
  }
  capTableScenario: (args: {
    name: String
  }) => CapTableScenarioObservableChain & {
    execute: (
      request: CapTableScenarioRequest,
      defaultValue?: CapTableScenario | null,
    ) => Observable<CapTableScenario | null>
  }
  capTableScenarios: {
    execute: (
      request: CapTableScenarioRequest,
      defaultValue?: (CapTableScenario | null)[] | null,
    ) => Observable<(CapTableScenario | null)[] | null>
  }
  company: ((args?: {
    id?: UUID | null
    asOfDate?: Date | null
    aggregationType?: TagsTagTypeChoices | null
    aggregationId?: UUID | null
    currency?: String | null
  }) => CompanyObservableChain & {
    execute: (request: CompanyRequest, defaultValue?: Company | null) => Observable<Company | null>
  }) &
    (CompanyObservableChain & {
      execute: (request: CompanyRequest, defaultValue?: Company | null) => Observable<Company | null>
    })
  companyByDocId: (args: {
    documentId: String
  }) => CompanyObservableChain & {
    execute: (request: CompanyRequest, defaultValue?: Company | null) => Observable<Company | null>
  }
  companies: { execute: (request: CompanyRequest, defaultValue?: Company[]) => Observable<Company[]> }
  nodeApiToken: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
}

export interface UserPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  email: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  firstName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  middleName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  lastName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  remoteId: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital | null) => Promise<VentureCapital | null>
  }
  profileLp: ProfileLimitedPartnerPromiseChain & {
    execute: (
      request: ProfileLimitedPartnerRequest,
      defaultValue?: ProfileLimitedPartner | null,
    ) => Promise<ProfileLimitedPartner | null>
  }
  deviceId: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  role: { execute: (request?: boolean | number, defaultValue?: Roles) => Promise<Roles> }
  shortName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  lpInvestments: { execute: (request: LPInvestmentRequest, defaultValue?: LPInvestment[]) => Promise<LPInvestment[]> }
  belongsToPlatform: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  getHelpUrl: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  navigationPermissions: NavigationPermissionsPromiseChain & {
    execute: (
      request: NavigationPermissionsRequest,
      defaultValue?: NavigationPermissions | null,
    ) => Promise<NavigationPermissions | null>
  }
}

export interface UserObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  email: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  firstName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  middleName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  lastName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  remoteId: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital | null) => Observable<VentureCapital | null>
  }
  profileLp: ProfileLimitedPartnerObservableChain & {
    execute: (
      request: ProfileLimitedPartnerRequest,
      defaultValue?: ProfileLimitedPartner | null,
    ) => Observable<ProfileLimitedPartner | null>
  }
  deviceId: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  role: { execute: (request?: boolean | number, defaultValue?: Roles) => Observable<Roles> }
  shortName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  lpInvestments: { execute: (request: LPInvestmentRequest, defaultValue?: LPInvestment[]) => Observable<LPInvestment[]> }
  belongsToPlatform: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  getHelpUrl: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  navigationPermissions: NavigationPermissionsObservableChain & {
    execute: (
      request: NavigationPermissionsRequest,
      defaultValue?: NavigationPermissions | null,
    ) => Observable<NavigationPermissions | null>
  }
}

export interface VentureCapitalPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  siteMode: {
    execute: (
      request?: boolean | number,
      defaultValue?: VentureCapitalsVentureCapitalSiteModeChoices,
    ) => Promise<VentureCapitalsVentureCapitalSiteModeChoices>
  }
  companies: { execute: (request: CompanyRequest, defaultValue?: Company[]) => Promise<Company[]> }
}

export interface VentureCapitalObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  siteMode: {
    execute: (
      request?: boolean | number,
      defaultValue?: VentureCapitalsVentureCapitalSiteModeChoices,
    ) => Observable<VentureCapitalsVentureCapitalSiteModeChoices>
  }
  companies: { execute: (request: CompanyRequest, defaultValue?: Company[]) => Observable<Company[]> }
}

export interface CompanyPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  documentId: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  documentVersionId: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  documentsDump: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  fdSharesAsInt: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  partner: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  rawCurrency: {
    execute: (
      request?: boolean | number,
      defaultValue?: CompaniesCompanyRawCurrencyChoices | null,
    ) => Promise<CompaniesCompanyRawCurrencyChoices | null>
  }
  status: {
    execute: (
      request?: boolean | number,
      defaultValue?: CompaniesCompanyStatusChoices,
    ) => Promise<CompaniesCompanyStatusChoices>
  }
  statusDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  totalShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  useManualValuations: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  user: UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User) => Promise<User> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  companyType: {
    execute: (
      request?: boolean | number,
      defaultValue?: CompaniesCompanyCompanyTypeChoices,
    ) => Promise<CompaniesCompanyCompanyTypeChoices>
  }
  ceoName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  contactName1: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  contactName2: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  phoneNumber: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  email: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  headquarters: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  address1: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  address2: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  city: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  state: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  country: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  postalCode: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  url: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  description: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  foundingDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  blurb: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  update: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  sector: SectorPromiseChain & { execute: (request: SectorRequest, defaultValue?: Sector | null) => Promise<Sector | null> }
  exitDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  updatingUser: UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User | null) => Promise<User | null> }
  updateDate: { execute: (request?: boolean | number, defaultValue?: Date) => Promise<Date> }
  importSuccessStatus: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  entitiesCompanyQrId: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  isAiCaptable: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  logo: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  bgPhoto: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  clonedVersions: {
    execute: (request: ClonedVersionRequest, defaultValue?: ClonedVersion[] | null) => Promise<ClonedVersion[] | null>
  }
  allFundInvestments: {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Promise<FundInvestment[]>
  }
  aggregationType: {
    execute: (request?: boolean | number, defaultValue?: TagsTagTypeChoices | null) => Promise<TagsTagTypeChoices | null>
  }
  aggregationId: { execute: (request?: boolean | number, defaultValue?: UUID | null) => Promise<UUID | null> }
  asOfDate: { execute: (request?: boolean | number, defaultValue?: Date) => Promise<Date> }
  capTableScenarios: {
    execute: (request: CapTableScenarioRequest, defaultValue?: CapTableScenario[]) => Promise<CapTableScenario[]>
  }
  currency: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  dashboardSettings: SettingsPromiseChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Promise<Settings>
  }
  defaultDashboardSettings: SettingsPromiseChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Promise<Settings>
  }
  effectiveDate: { execute: (request?: boolean | number, defaultValue?: DateTime | null) => Promise<DateTime | null> }
  fullAddress: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  fundInvestments: {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Promise<FundInvestment[]>
  }
  inactivityDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  investmentDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  investmentsStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsFundInvestmentStatusChoices | null,
    ) => Promise<FundInvestmentsFundInvestmentStatusChoices | null>
  }
  investmentsStatusDisplay: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  irr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  ipoName: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  jwtToken: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  maxEv: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  minEv: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  nav: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  ownershipPercent: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  realizedValue: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  redeemedShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sentiment: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  sharePrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  topHolders: { execute: (request: TopHolderRequest, defaultValue?: TopHolder[]) => Promise<TopHolder[]> }
  totalProceeds: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalValue: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
}

export interface CompanyObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  documentId: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  documentVersionId: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  documentsDump: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  fdSharesAsInt: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  partner: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  rawCurrency: {
    execute: (
      request?: boolean | number,
      defaultValue?: CompaniesCompanyRawCurrencyChoices | null,
    ) => Observable<CompaniesCompanyRawCurrencyChoices | null>
  }
  status: {
    execute: (
      request?: boolean | number,
      defaultValue?: CompaniesCompanyStatusChoices,
    ) => Observable<CompaniesCompanyStatusChoices>
  }
  statusDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  totalShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  useManualValuations: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  user: UserObservableChain & { execute: (request: UserRequest, defaultValue?: User) => Observable<User> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  companyType: {
    execute: (
      request?: boolean | number,
      defaultValue?: CompaniesCompanyCompanyTypeChoices,
    ) => Observable<CompaniesCompanyCompanyTypeChoices>
  }
  ceoName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  contactName1: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  contactName2: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  phoneNumber: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  email: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  headquarters: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  address1: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  address2: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  city: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  state: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  country: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  postalCode: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  url: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  description: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  foundingDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  blurb: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  update: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  sector: SectorObservableChain & {
    execute: (request: SectorRequest, defaultValue?: Sector | null) => Observable<Sector | null>
  }
  exitDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  updatingUser: UserObservableChain & {
    execute: (request: UserRequest, defaultValue?: User | null) => Observable<User | null>
  }
  updateDate: { execute: (request?: boolean | number, defaultValue?: Date) => Observable<Date> }
  importSuccessStatus: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  entitiesCompanyQrId: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  isAiCaptable: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  logo: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  bgPhoto: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  clonedVersions: {
    execute: (request: ClonedVersionRequest, defaultValue?: ClonedVersion[] | null) => Observable<ClonedVersion[] | null>
  }
  allFundInvestments: {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Observable<FundInvestment[]>
  }
  aggregationType: {
    execute: (request?: boolean | number, defaultValue?: TagsTagTypeChoices | null) => Observable<TagsTagTypeChoices | null>
  }
  aggregationId: { execute: (request?: boolean | number, defaultValue?: UUID | null) => Observable<UUID | null> }
  asOfDate: { execute: (request?: boolean | number, defaultValue?: Date) => Observable<Date> }
  capTableScenarios: {
    execute: (request: CapTableScenarioRequest, defaultValue?: CapTableScenario[]) => Observable<CapTableScenario[]>
  }
  currency: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  dashboardSettings: SettingsObservableChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Observable<Settings>
  }
  defaultDashboardSettings: SettingsObservableChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Observable<Settings>
  }
  effectiveDate: { execute: (request?: boolean | number, defaultValue?: DateTime | null) => Observable<DateTime | null> }
  fullAddress: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  fundInvestments: {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Observable<FundInvestment[]>
  }
  inactivityDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  investmentDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  investmentsStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsFundInvestmentStatusChoices | null,
    ) => Observable<FundInvestmentsFundInvestmentStatusChoices | null>
  }
  investmentsStatusDisplay: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  irr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  ipoName: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  jwtToken: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  maxEv: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  minEv: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  nav: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  ownershipPercent: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  realizedValue: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  redeemedShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sentiment: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  sharePrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  topHolders: { execute: (request: TopHolderRequest, defaultValue?: TopHolder[]) => Observable<TopHolder[]> }
  totalProceeds: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalValue: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
}

export interface SectorPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companySet: { execute: (request: CompanyRequest, defaultValue?: Company[]) => Promise<Company[]> }
}

export interface SectorObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companySet: { execute: (request: CompanyRequest, defaultValue?: Company[]) => Observable<Company[]> }
}

/** Data for the clones created from the Company's Captable. */
export interface ClonedVersionPromiseChain {
  clonedCapTableUrl: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  clonedCapTableName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
}

/** Data for the clones created from the Company's Captable. */
export interface ClonedVersionObservableChain {
  clonedCapTableUrl: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  clonedCapTableName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
}

export interface FundInvestmentPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  fund: FundPromiseChain & { execute: (request: FundRequest, defaultValue?: Fund) => Promise<Fund> }
  calculationStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsFundInvestmentCalculationStatusChoices,
    ) => Promise<FundInvestmentsFundInvestmentCalculationStatusChoices>
  }
  company: CompanyPromiseChain & { execute: (request: CompanyRequest, defaultValue?: Company) => Promise<Company> }
  status: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsFundInvestmentStatusChoices,
    ) => Promise<FundInvestmentsFundInvestmentStatusChoices>
  }
  investmentDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  inactivityDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  includeInScenariosCalcs: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  ipoName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  redeemedShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  escrow: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  escrowDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  comments: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  sharePrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  capTableUrl: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  ev: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentMemos: {
    execute: (request: InvestmentMemoRequest, defaultValue?: InvestmentMemo[]) => Promise<InvestmentMemo[]>
  }
  irr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  nav: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  ownershipPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  realizedValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  totalValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  totalProceeds: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  url: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  statusDisplay: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  scenarios: {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: FundScenarioInvestmentScenario[],
    ) => Promise<FundScenarioInvestmentScenario[]>
  }
  scenariosOwnFundAndParent: {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: FundScenarioInvestmentScenario[],
    ) => Promise<FundScenarioInvestmentScenario[]>
  }
  companyEvs: { execute: (request: CompanyEVRequest, defaultValue?: CompanyEV[]) => Promise<CompanyEV[]> }
  manualCompanyEv: CompanyEVPromiseChain & {
    execute: (request: CompanyEVRequest, defaultValue?: CompanyEV | null) => Promise<CompanyEV | null>
  }
}

export interface FundInvestmentObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  fund: FundObservableChain & { execute: (request: FundRequest, defaultValue?: Fund) => Observable<Fund> }
  calculationStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsFundInvestmentCalculationStatusChoices,
    ) => Observable<FundInvestmentsFundInvestmentCalculationStatusChoices>
  }
  company: CompanyObservableChain & { execute: (request: CompanyRequest, defaultValue?: Company) => Observable<Company> }
  status: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsFundInvestmentStatusChoices,
    ) => Observable<FundInvestmentsFundInvestmentStatusChoices>
  }
  investmentDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  inactivityDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  includeInScenariosCalcs: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  ipoName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  redeemedShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  escrow: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  escrowDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  comments: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  sharePrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  capTableUrl: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  ev: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentMemos: {
    execute: (request: InvestmentMemoRequest, defaultValue?: InvestmentMemo[]) => Observable<InvestmentMemo[]>
  }
  irr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  nav: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  ownershipPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  realizedValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  totalValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  totalProceeds: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  url: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  statusDisplay: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  scenarios: {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: FundScenarioInvestmentScenario[],
    ) => Observable<FundScenarioInvestmentScenario[]>
  }
  scenariosOwnFundAndParent: {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: FundScenarioInvestmentScenario[],
    ) => Observable<FundScenarioInvestmentScenario[]>
  }
  companyEvs: { execute: (request: CompanyEVRequest, defaultValue?: CompanyEV[]) => Observable<CompanyEV[]> }
  manualCompanyEv: CompanyEVObservableChain & {
    execute: (request: CompanyEVRequest, defaultValue?: CompanyEV | null) => Observable<CompanyEV | null>
  }
}

export interface FundPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  gp: UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User) => Promise<User> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  description: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  isInactive: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  inactivityDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  isSubfund: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  parentFund: FundPromiseChain & { execute: (request: FundRequest, defaultValue?: Fund | null) => Promise<Fund | null> }
  interestRate: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  managementFee: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  carriedInterest: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  closeDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  useManualIrr: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  apiFundId: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  useExpensesInIrr: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  useExpensesInRvpi: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  lpinvestmentSet: { execute: (request: LPInvestmentRequest, defaultValue?: LPInvestment[]) => Promise<LPInvestment[]> }
  subfunds: { execute: (request: FundRequest, defaultValue?: Fund[]) => Promise<Fund[]> }
  fundscenarioSet: { execute: (request: FundScenarioRequest, defaultValue?: FundScenario[]) => Promise<FundScenario[]> }
  asOfDate: { execute: (request?: boolean | number, defaultValue?: Date) => Promise<Date> }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  capitalCalled: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  companiesCount: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  rawCurrency: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  currency: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  currentLpInvestment: LPInvestmentPromiseChain & {
    execute: (request: LPInvestmentRequest, defaultValue?: LPInvestment | null) => Promise<LPInvestment | null>
  }
  dashboardSettings: SettingsPromiseChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Promise<Settings>
  }
  defaultDashboardSettings: SettingsPromiseChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Promise<Settings>
  }
  dpi: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  divisions: { execute: (request: TagRequest, defaultValue?: Tag[]) => Promise<Tag[]> }
  fundInvestments: {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Promise<FundInvestment[]>
  }
  grossIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  grossMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  grossRealizedMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  groups: { execute: (request: TagRequest, defaultValue?: Tag[]) => Promise<Tag[]> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentsBySector: {
    execute: (request: ChartDataItemRequest, defaultValue?: ChartDataItem[]) => Promise<ChartDataItem[]>
  }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  netTvpi: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  netIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  realizedValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  rvpi: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  scenarios: {
    execute: (request: FundScenarioRequest, defaultValue?: (FundScenario | null)[]) => Promise<(FundScenario | null)[]>
  }
  totalCommittedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface FundObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  gp: UserObservableChain & { execute: (request: UserRequest, defaultValue?: User) => Observable<User> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  description: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  isInactive: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  inactivityDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  isSubfund: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  parentFund: FundObservableChain & {
    execute: (request: FundRequest, defaultValue?: Fund | null) => Observable<Fund | null>
  }
  interestRate: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  managementFee: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  carriedInterest: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  closeDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  useManualIrr: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  apiFundId: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  useExpensesInIrr: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  useExpensesInRvpi: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  lpinvestmentSet: { execute: (request: LPInvestmentRequest, defaultValue?: LPInvestment[]) => Observable<LPInvestment[]> }
  subfunds: { execute: (request: FundRequest, defaultValue?: Fund[]) => Observable<Fund[]> }
  fundscenarioSet: { execute: (request: FundScenarioRequest, defaultValue?: FundScenario[]) => Observable<FundScenario[]> }
  asOfDate: { execute: (request?: boolean | number, defaultValue?: Date) => Observable<Date> }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  capitalCalled: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  companiesCount: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  rawCurrency: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  currency: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  currentLpInvestment: LPInvestmentObservableChain & {
    execute: (request: LPInvestmentRequest, defaultValue?: LPInvestment | null) => Observable<LPInvestment | null>
  }
  dashboardSettings: SettingsObservableChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Observable<Settings>
  }
  defaultDashboardSettings: SettingsObservableChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Observable<Settings>
  }
  dpi: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  divisions: { execute: (request: TagRequest, defaultValue?: Tag[]) => Observable<Tag[]> }
  fundInvestments: {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment[]) => Observable<FundInvestment[]>
  }
  grossIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  grossMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  grossRealizedMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  groups: { execute: (request: TagRequest, defaultValue?: Tag[]) => Observable<Tag[]> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentsBySector: {
    execute: (request: ChartDataItemRequest, defaultValue?: ChartDataItem[]) => Observable<ChartDataItem[]>
  }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  netTvpi: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  netIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  realizedValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  rvpi: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  scenarios: {
    execute: (request: FundScenarioRequest, defaultValue?: (FundScenario | null)[]) => Observable<(FundScenario | null)[]>
  }
  totalCommittedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface LPInvestmentPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  lp: UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User) => Promise<User> }
  fund: FundPromiseChain & { execute: (request: FundRequest, defaultValue?: Fund) => Promise<Fund> }
  committedDate: { execute: (request?: boolean | number, defaultValue?: Date) => Promise<Date> }
  accruedInterest: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  note: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  committedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  committedCapitalAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  committedCapitalZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amount: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  distributions: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  returnPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface LPInvestmentObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  lp: UserObservableChain & { execute: (request: UserRequest, defaultValue?: User) => Observable<User> }
  fund: FundObservableChain & { execute: (request: FundRequest, defaultValue?: Fund) => Observable<Fund> }
  committedDate: { execute: (request?: boolean | number, defaultValue?: Date) => Observable<Date> }
  accruedInterest: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  note: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  committedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  committedCapitalAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  committedCapitalZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amount: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  distributions: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  returnPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface FundScenarioPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  defaultDilution: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  defaultExitDate: { execute: (request?: boolean | number, defaultValue?: Date) => Promise<Date> }
  fund: FundPromiseChain & { execute: (request: FundRequest, defaultValue?: Fund) => Promise<Fund> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  position: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  companyevSet: { execute: (request: CompanyEVRequest, defaultValue?: CompanyEV[]) => Promise<CompanyEV[]> }
  investmentmemoSet: {
    execute: (request: InvestmentMemoRequest, defaultValue?: InvestmentMemo[]) => Promise<InvestmentMemo[]>
  }
  fundscenarioinvestmentscenarioSet: {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: FundScenarioInvestmentScenario[],
    ) => Promise<FundScenarioInvestmentScenario[]>
  }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  calculationStatus: {
    execute: (request?: boolean | number, defaultValue?: CalculationStatuses | null) => Promise<CalculationStatuses | null>
  }
  grossIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  grossMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
}

export interface FundScenarioObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  defaultDilution: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  defaultExitDate: { execute: (request?: boolean | number, defaultValue?: Date) => Observable<Date> }
  fund: FundObservableChain & { execute: (request: FundRequest, defaultValue?: Fund) => Observable<Fund> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  position: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  companyevSet: { execute: (request: CompanyEVRequest, defaultValue?: CompanyEV[]) => Observable<CompanyEV[]> }
  investmentmemoSet: {
    execute: (request: InvestmentMemoRequest, defaultValue?: InvestmentMemo[]) => Observable<InvestmentMemo[]>
  }
  fundscenarioinvestmentscenarioSet: {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: FundScenarioInvestmentScenario[],
    ) => Observable<FundScenarioInvestmentScenario[]>
  }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  calculationStatus: {
    execute: (
      request?: boolean | number,
      defaultValue?: CalculationStatuses | null,
    ) => Observable<CalculationStatuses | null>
  }
  grossIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  grossMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
}

export interface CompanyEVPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  asOfDate: { execute: (request?: boolean | number, defaultValue?: Date) => Promise<Date> }
  fdShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fundScenario: FundScenarioPromiseChain & {
    execute: (request: FundScenarioRequest, defaultValue?: FundScenario | null) => Promise<FundScenario | null>
  }
  investment: FundInvestmentPromiseChain & {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment) => Promise<FundInvestment>
  }
  isCreatedManually: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  source: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsCompanyEVSourceChoices,
    ) => Promise<FundInvestmentsCompanyEVSourceChoices>
  }
  totalOwnership: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  type: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsCompanyEVTypeChoices,
    ) => Promise<FundInvestmentsCompanyEVTypeChoices>
  }
  useNav: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  rawCurrency: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsCompanyEVRawCurrencyChoices | null,
    ) => Promise<FundInvestmentsCompanyEVRawCurrencyChoices | null>
  }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  ev: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  nav: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  sharePrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValuePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  bookValueZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  evZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  navZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  sharePriceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
}

export interface CompanyEVObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  asOfDate: { execute: (request?: boolean | number, defaultValue?: Date) => Observable<Date> }
  fdShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fundScenario: FundScenarioObservableChain & {
    execute: (request: FundScenarioRequest, defaultValue?: FundScenario | null) => Observable<FundScenario | null>
  }
  investment: FundInvestmentObservableChain & {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment) => Observable<FundInvestment>
  }
  isCreatedManually: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  source: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsCompanyEVSourceChoices,
    ) => Observable<FundInvestmentsCompanyEVSourceChoices>
  }
  totalOwnership: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  type: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsCompanyEVTypeChoices,
    ) => Observable<FundInvestmentsCompanyEVTypeChoices>
  }
  useNav: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  rawCurrency: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsCompanyEVRawCurrencyChoices | null,
    ) => Observable<FundInvestmentsCompanyEVRawCurrencyChoices | null>
  }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  ev: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  nav: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  sharePrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValuePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  bookValueZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  evZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  navZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  sharePriceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
}

export interface InvestmentMemoPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  advisoryInNav: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  advisoryShares: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsInvestmentMemoAdvisorySharesChoices | null,
    ) => Promise<FundInvestmentsInvestmentMemoAdvisorySharesChoices | null>
  }
  advisorySharesAmount: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  advisorySharesType: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsInvestmentMemoAdvisorySharesTypeChoices | null,
    ) => Promise<FundInvestmentsInvestmentMemoAdvisorySharesTypeChoices | null>
  }
  antiDilution: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  boardSeat: { execute: (request?: boolean | number, defaultValue?: Int | null) => Promise<Int | null> }
  cnoteConversionDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  companyTotalShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  debtConvLanguage: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  dragAlong: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  employeePool: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  equityDilution: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fundParticipated: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  fundScenario: FundScenarioPromiseChain & {
    execute: (request: FundScenarioRequest, defaultValue?: FundScenario | null) => Promise<FundScenario | null>
  }
  informationRights: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  interest: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  investment: FundInvestmentPromiseChain & {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment) => Promise<FundInvestment>
  }
  irr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  leadInvestor: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  liquidationPreference: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  notes: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  payToPlay: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  participationRights: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  preferenceLanguage: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  proRata: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  protectiveProvisions: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  sharesPurchased: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  transactionDate: { execute: (request?: boolean | number, defaultValue?: Date) => Promise<Date> }
  transactionName: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  transactionType: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsInvestmentMemoTransactionTypeChoices,
    ) => Promise<FundInvestmentsInvestmentMemoTransactionTypeChoices>
  }
  warrants: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantsInNav: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  rawCurrency: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsInvestmentMemoRawCurrencyChoices | null,
    ) => Promise<FundInvestmentsInvestmentMemoRawCurrencyChoices | null>
  }
  amount: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  companyTotalRaise: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuation: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  fmvPps: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuation: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  price: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceeds: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  totalRaised: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCap: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  amountZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaisePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyTotalRaiseZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  companyValuationZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fmvPpsZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  premoneyValuationZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  pricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  priceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  proceedsZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalRaisedZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  valuationCapZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  warrantPriceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
}

export interface InvestmentMemoObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  advisoryInNav: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  advisoryShares: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsInvestmentMemoAdvisorySharesChoices | null,
    ) => Observable<FundInvestmentsInvestmentMemoAdvisorySharesChoices | null>
  }
  advisorySharesAmount: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  advisorySharesType: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsInvestmentMemoAdvisorySharesTypeChoices | null,
    ) => Observable<FundInvestmentsInvestmentMemoAdvisorySharesTypeChoices | null>
  }
  antiDilution: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  boardSeat: { execute: (request?: boolean | number, defaultValue?: Int | null) => Observable<Int | null> }
  cnoteConversionDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  companyTotalShares: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  debtConvLanguage: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  dragAlong: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  employeePool: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  equityDilution: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fundParticipated: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  fundScenario: FundScenarioObservableChain & {
    execute: (request: FundScenarioRequest, defaultValue?: FundScenario | null) => Observable<FundScenario | null>
  }
  informationRights: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  interest: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  investment: FundInvestmentObservableChain & {
    execute: (request: FundInvestmentRequest, defaultValue?: FundInvestment) => Observable<FundInvestment>
  }
  irr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  leadInvestor: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  liquidationPreference: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  notes: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  payToPlay: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  participationRights: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  preferenceLanguage: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  proRata: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  protectiveProvisions: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  sharesPurchased: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  transactionDate: { execute: (request?: boolean | number, defaultValue?: Date) => Observable<Date> }
  transactionName: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  transactionType: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsInvestmentMemoTransactionTypeChoices,
    ) => Observable<FundInvestmentsInvestmentMemoTransactionTypeChoices>
  }
  warrants: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantsInNav: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  rawCurrency: {
    execute: (
      request?: boolean | number,
      defaultValue?: FundInvestmentsInvestmentMemoRawCurrencyChoices | null,
    ) => Observable<FundInvestmentsInvestmentMemoRawCurrencyChoices | null>
  }
  amount: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  companyTotalRaise: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuation: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  fmvPps: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuation: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  price: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceeds: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  totalRaised: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCap: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPrice: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  amountZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaisePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyTotalRaiseZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  companyValuationZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fmvPpsZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  premoneyValuationZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  pricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  priceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  proceedsZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalRaisedZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapPhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  valuationCapZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceAud: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceBrl: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceCad: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceChf: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceCny: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceCop: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceDkk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceEur: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceGbp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceIdr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceIls: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceInr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceJpy: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceKrw: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceMmk: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceMxn: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceMyr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceNok: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceNzd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPricePhp: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceSek: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceSgd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceTry: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceUsd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceVnd: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  warrantPriceZar: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
}

export interface FundScenarioInvestmentScenarioPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID | null) => Promise<UUID | null> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  capTableScenario: CapTableScenarioPromiseChain & {
    execute: (request: CapTableScenarioRequest, defaultValue?: CapTableScenario | null) => Promise<CapTableScenario | null>
  }
  exitAmount: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  exitDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Promise<Date | null> }
  dilution: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  fundScenario: FundScenarioPromiseChain & {
    execute: (request: FundScenarioRequest, defaultValue?: FundScenario) => Promise<FundScenario>
  }
  nav: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  irr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  position: { execute: (request?: boolean | number, defaultValue?: Int | null) => Promise<Int | null> }
}

export interface FundScenarioInvestmentScenarioObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID | null) => Observable<UUID | null> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  capTableScenario: CapTableScenarioObservableChain & {
    execute: (
      request: CapTableScenarioRequest,
      defaultValue?: CapTableScenario | null,
    ) => Observable<CapTableScenario | null>
  }
  exitAmount: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  exitDate: { execute: (request?: boolean | number, defaultValue?: Date | null) => Observable<Date | null> }
  dilution: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  fundScenario: FundScenarioObservableChain & {
    execute: (request: FundScenarioRequest, defaultValue?: FundScenario) => Observable<FundScenario>
  }
  nav: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  irr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  position: { execute: (request?: boolean | number, defaultValue?: Int | null) => Observable<Int | null> }
}

export interface CapTableScenarioPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  company: CompanyPromiseChain & {
    execute: (request: CompanyRequest, defaultValue?: Company | null) => Promise<Company | null>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  fundscenarioinvestmentscenarioSet: {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: FundScenarioInvestmentScenario[],
    ) => Promise<FundScenarioInvestmentScenario[]>
  }
}

export interface CapTableScenarioObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  company: CompanyObservableChain & {
    execute: (request: CompanyRequest, defaultValue?: Company | null) => Observable<Company | null>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  fundscenarioinvestmentscenarioSet: {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: FundScenarioInvestmentScenario[],
    ) => Observable<FundScenarioInvestmentScenario[]>
  }
}

export interface SettingsPromiseChain {
  dashboardType: { execute: (request?: boolean | number, defaultValue?: Types) => Promise<Types> }
  sections: { execute: (request: SectionRequest, defaultValue?: Section[]) => Promise<Section[]> }
}

export interface SettingsObservableChain {
  dashboardType: { execute: (request?: boolean | number, defaultValue?: Types) => Observable<Types> }
  sections: { execute: (request: SectionRequest, defaultValue?: Section[]) => Observable<Section[]> }
}

export interface SectionPromiseChain {
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  order: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  checked: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  components: { execute: (request: ComponentRequest, defaultValue?: Component[]) => Promise<Component[]> }
}

export interface SectionObservableChain {
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  order: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  checked: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  components: { execute: (request: ComponentRequest, defaultValue?: Component[]) => Observable<Component[]> }
}

export interface ComponentPromiseChain {
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  order: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  checked: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
}

export interface ComponentObservableChain {
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  order: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  checked: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
}

export interface TagPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  type: {
    execute: (request?: boolean | number, defaultValue?: TagsTagTypeChoices | null) => Promise<TagsTagTypeChoices | null>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
}

export interface TagObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  type: {
    execute: (request?: boolean | number, defaultValue?: TagsTagTypeChoices | null) => Observable<TagsTagTypeChoices | null>
  }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
}

export interface ChartDataItemPromiseChain {
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  y: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
}

export interface ChartDataItemObservableChain {
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  y: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
}

export interface TopHolderPromiseChain {
  color: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  holdingsAmount: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  holdingsPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
}

export interface TopHolderObservableChain {
  color: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  holdingsAmount: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  holdingsPercent: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
}

export interface ProfileLimitedPartnerPromiseChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Promise<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Promise<DateTime> }
  ventureCapital: VentureCapitalPromiseChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Promise<VentureCapital>
  }
  status: {
    execute: (
      request?: boolean | number,
      defaultValue?: UsersProfileLimitedPartnerStatusChoices,
    ) => Promise<UsersProfileLimitedPartnerStatusChoices>
  }
  user: UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User) => Promise<User> }
  gp: UserPromiseChain & { execute: (request: UserRequest, defaultValue?: User | null) => Promise<User | null> }
  baseCurrency: {
    execute: (
      request?: boolean | number,
      defaultValue?: UsersProfileLimitedPartnerBaseCurrencyChoices,
    ) => Promise<UsersProfileLimitedPartnerBaseCurrencyChoices>
  }
  legalEntity: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  identifier: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  address1: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  address2: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  city: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  state: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  postalCode: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  phoneNumber1: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  phoneNumber2: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  prefContactName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  prefContactTitle: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  prefContactEmail: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  prefContactPhone: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  bankName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  bankAbaNumber: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  bankAccountNumber: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  additionalInstructions: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  accruedPreference: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  committedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  contributedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  distributedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  nav: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  returnPercent: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
}

export interface ProfileLimitedPartnerObservableChain {
  id: { execute: (request?: boolean | number, defaultValue?: UUID) => Observable<UUID> }
  dateCreated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  dateUpdated: { execute: (request?: boolean | number, defaultValue?: DateTime) => Observable<DateTime> }
  ventureCapital: VentureCapitalObservableChain & {
    execute: (request: VentureCapitalRequest, defaultValue?: VentureCapital) => Observable<VentureCapital>
  }
  status: {
    execute: (
      request?: boolean | number,
      defaultValue?: UsersProfileLimitedPartnerStatusChoices,
    ) => Observable<UsersProfileLimitedPartnerStatusChoices>
  }
  user: UserObservableChain & { execute: (request: UserRequest, defaultValue?: User) => Observable<User> }
  gp: UserObservableChain & { execute: (request: UserRequest, defaultValue?: User | null) => Observable<User | null> }
  baseCurrency: {
    execute: (
      request?: boolean | number,
      defaultValue?: UsersProfileLimitedPartnerBaseCurrencyChoices,
    ) => Observable<UsersProfileLimitedPartnerBaseCurrencyChoices>
  }
  legalEntity: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  identifier: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  address1: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  address2: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  city: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  state: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  postalCode: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  phoneNumber1: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  phoneNumber2: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  prefContactName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  prefContactTitle: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  prefContactEmail: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  prefContactPhone: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  bankName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  bankAbaNumber: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  bankAccountNumber: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  additionalInstructions: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  accruedPreference: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  committedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  contributedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  distributedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  nav: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  returnPercent: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
}

/**
 * Used to denote which navigation tabs to be displayed on the front-end.
 * Also used by the backend, if incase backend authorization is needed.
 *
 * Note: This is configured on the `User` model.
 */
export interface NavigationPermissionsPromiseChain {
  isSiteModeDefault: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  isSiteModeLite: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  isLimitedPartner: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  isGpOrPm: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  isCompanyPageAccessible: {
    execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null>
  }
}

/**
 * Used to denote which navigation tabs to be displayed on the front-end.
 * Also used by the backend, if incase backend authorization is needed.
 *
 * Note: This is configured on the `User` model.
 */
export interface NavigationPermissionsObservableChain {
  isSiteModeDefault: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  isSiteModeLite: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  isLimitedPartner: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  isGpOrPm: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  isCompanyPageAccessible: {
    execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null>
  }
}

/** Type declaration for User's login meta information. */
export interface LoginMetaPromiseChain {
  oidcLoginUrl: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  ssoLoginUrl: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  nonPlatformLoginExists: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  platformLoginExists: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
}

/** Type declaration for User's login meta information. */
export interface LoginMetaObservableChain {
  oidcLoginUrl: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  ssoLoginUrl: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  nonPlatformLoginExists: {
    execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null>
  }
  platformLoginExists: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
}

export interface FundAggregationPromiseChain {
  aggregationType: {
    execute: (request?: boolean | number, defaultValue?: TagsTagTypeChoices | null) => Promise<TagsTagTypeChoices | null>
  }
  aggregationId: { execute: (request?: boolean | number, defaultValue?: UUID | null) => Promise<UUID | null> }
  asOfDate: { execute: (request?: boolean | number, defaultValue?: Date) => Promise<Date> }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  capitalCalled: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  companiesCount: { execute: (request?: boolean | number, defaultValue?: Int) => Promise<Int> }
  currency: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  currentLpInvestment: LPInvestmentPromiseChain & {
    execute: (request: LPInvestmentRequest, defaultValue?: LPInvestment | null) => Promise<LPInvestment | null>
  }
  dashboardSettings: SettingsPromiseChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Promise<Settings>
  }
  defaultDashboardSettings: SettingsPromiseChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Promise<Settings>
  }
  dpi: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  funds: { execute: (request: FundRequest, defaultValue?: Fund[]) => Promise<Fund[]> }
  grossIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  grossMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  grossRealizedMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  investmentsBySector: {
    execute: (request: ChartDataItemRequest, defaultValue?: ChartDataItem[]) => Promise<ChartDataItem[]>
  }
  isSingleFund: { execute: (request?: boolean | number, defaultValue?: Boolean) => Promise<Boolean> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  netTvpi: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  netIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  realizedValue: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  rvpi: { execute: (request?: boolean | number, defaultValue?: Float | null) => Promise<Float | null> }
  totalCommittedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Promise<Float> }
  clonedCaptables: {
    execute: (request: ClonedCaptableRequest, defaultValue?: ClonedCaptable[] | null) => Promise<ClonedCaptable[] | null>
  }
}

export interface FundAggregationObservableChain {
  aggregationType: {
    execute: (request?: boolean | number, defaultValue?: TagsTagTypeChoices | null) => Observable<TagsTagTypeChoices | null>
  }
  aggregationId: { execute: (request?: boolean | number, defaultValue?: UUID | null) => Observable<UUID | null> }
  asOfDate: { execute: (request?: boolean | number, defaultValue?: Date) => Observable<Date> }
  bookValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  capitalCalled: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  companiesCount: { execute: (request?: boolean | number, defaultValue?: Int) => Observable<Int> }
  currency: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  currentLpInvestment: LPInvestmentObservableChain & {
    execute: (request: LPInvestmentRequest, defaultValue?: LPInvestment | null) => Observable<LPInvestment | null>
  }
  dashboardSettings: SettingsObservableChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Observable<Settings>
  }
  defaultDashboardSettings: SettingsObservableChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Observable<Settings>
  }
  dpi: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  funds: { execute: (request: FundRequest, defaultValue?: Fund[]) => Observable<Fund[]> }
  grossIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  grossMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  grossRealizedMultiple: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  investedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  investmentsBySector: {
    execute: (request: ChartDataItemRequest, defaultValue?: ChartDataItem[]) => Observable<ChartDataItem[]>
  }
  isSingleFund: { execute: (request?: boolean | number, defaultValue?: Boolean) => Observable<Boolean> }
  multiple: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  name: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  netTvpi: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  netIrr: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  realizedValue: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  rvpi: { execute: (request?: boolean | number, defaultValue?: Float | null) => Observable<Float | null> }
  totalCommittedCapital: { execute: (request?: boolean | number, defaultValue?: Float) => Observable<Float> }
  clonedCaptables: {
    execute: (request: ClonedCaptableRequest, defaultValue?: ClonedCaptable[] | null) => Observable<ClonedCaptable[] | null>
  }
}

/** Data for the clones of companies under the Fund. */
export interface ClonedCaptablePromiseChain {
  clonedCapTableUrl: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  clonedCapTableName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
  clonedParentName: { execute: (request?: boolean | number, defaultValue?: String) => Promise<String> }
}

/** Data for the clones of companies under the Fund. */
export interface ClonedCaptableObservableChain {
  clonedCapTableUrl: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  clonedCapTableName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
  clonedParentName: { execute: (request?: boolean | number, defaultValue?: String) => Observable<String> }
}

export interface MutationPromiseChain {
  sandBoxRestore: (args: {
    sourceId: UUID
    targetId: UUID
  }) => { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  login: (args: {
    email: String
    password: String
  }) => LoginPromiseChain & { execute: (request: LoginRequest, defaultValue?: Login | null) => Promise<Login | null> }
  passwordChange: (args: {
    email: String
    oldPassword: String
    password1: String
    password2: String
  }) => PasswordChangePromiseChain & {
    execute: (request: PasswordChangeRequest, defaultValue?: PasswordChange | null) => Promise<PasswordChange | null>
  }
  fundDashboardSettingsUpdate: (args: {
    dashboardSettings: SectionInput[]
  }) => DashboardSettingsUpdatePromiseChain & {
    execute: (
      request: DashboardSettingsUpdateRequest,
      defaultValue?: DashboardSettingsUpdate | null,
    ) => Promise<DashboardSettingsUpdate | null>
  }
  fundInvestmentChange: (args: {
    fundInvestments: (FundInvestmentChangeItem | null)[]
  }) => {
    execute: (
      request: FundInvestmentRequest,
      defaultValue?: (FundInvestment | null)[] | null,
    ) => Promise<(FundInvestment | null)[] | null>
  }
  capTableReportGenerate: (args: {
    asOfDate: Date
    email: String
    investmentId: UUID
  }) => CapTableReportGeneratePromiseChain & {
    execute: (
      request: CapTableReportGenerateRequest,
      defaultValue?: CapTableReportGenerate | null,
    ) => Promise<CapTableReportGenerate | null>
  }
  capTableScenariosUpdate: (args: {
    scenarios: (CapTableScenarioItem | null)[]
  }) => {
    execute: (
      request: CapTableScenarioRequest,
      defaultValue?: (CapTableScenario | null)[] | null,
    ) => Promise<(CapTableScenario | null)[] | null>
  }
  fundScenariosAdd: (args: {
    scenarioSets: (FundScenariosAddSet | null)[]
  }) => {
    execute: (
      request: FundScenarioRequest,
      defaultValue?: (FundScenario | null)[] | null,
    ) => Promise<(FundScenario | null)[] | null>
  }
  fundScenariosChange: (args: {
    scenarios: (FundScenariosChangeItem | null)[]
  }) => {
    execute: (
      request: FundScenarioRequest,
      defaultValue?: (FundScenario | null)[] | null,
    ) => Promise<(FundScenario | null)[] | null>
  }
  /** Triggers datasync to calculate FundScenarios  */
  fundScenariosCalculate: (args: {
    fundId: UUID
    investmentId?: UUID | null
  }) => { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
  /** Deletes scenarios by list of IDs and returns amount of deleted rows.  */
  fundScenariosDelete: (args: {
    scenarios: (UUID | null)[]
  }) => { execute: (request?: boolean | number, defaultValue?: Int | null) => Promise<Int | null> }
  fundScenarioInvestmentScenariosAdd: (args: {
    scenarioSets: (FundScenarioInvestmentScenarioAddSet | null)[]
  }) => {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: (FundScenarioInvestmentScenario | null)[] | null,
    ) => Promise<(FundScenarioInvestmentScenario | null)[] | null>
  }
  fundScenarioInvestmentScenariosChange: (args: {
    scenarios: (FundScenarioInvestmentScenariosChangeItem | null)[]
  }) => {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: (FundScenarioInvestmentScenario | null)[] | null,
    ) => Promise<(FundScenarioInvestmentScenario | null)[] | null>
  }
  /** Deletes scenarios by list of IDs and returns amount of deleted rows.  */
  fundScenarioInvestmentScenariosDelete: (args: {
    scenarios: (UUID | null)[]
  }) => { execute: (request?: boolean | number, defaultValue?: Int | null) => Promise<Int | null> }
  companyDashboardSettingsUpdate: (args: {
    companyId: UUID
    dashboardSettings: SectionInput[]
  }) => DashboardSettingsUpdatePromiseChain & {
    execute: (
      request: DashboardSettingsUpdateRequest,
      defaultValue?: DashboardSettingsUpdate | null,
    ) => Promise<DashboardSettingsUpdate | null>
  }
}

export interface MutationObservableChain {
  sandBoxRestore: (args: {
    sourceId: UUID
    targetId: UUID
  }) => { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  login: (args: {
    email: String
    password: String
  }) => LoginObservableChain & { execute: (request: LoginRequest, defaultValue?: Login | null) => Observable<Login | null> }
  passwordChange: (args: {
    email: String
    oldPassword: String
    password1: String
    password2: String
  }) => PasswordChangeObservableChain & {
    execute: (request: PasswordChangeRequest, defaultValue?: PasswordChange | null) => Observable<PasswordChange | null>
  }
  fundDashboardSettingsUpdate: (args: {
    dashboardSettings: SectionInput[]
  }) => DashboardSettingsUpdateObservableChain & {
    execute: (
      request: DashboardSettingsUpdateRequest,
      defaultValue?: DashboardSettingsUpdate | null,
    ) => Observable<DashboardSettingsUpdate | null>
  }
  fundInvestmentChange: (args: {
    fundInvestments: (FundInvestmentChangeItem | null)[]
  }) => {
    execute: (
      request: FundInvestmentRequest,
      defaultValue?: (FundInvestment | null)[] | null,
    ) => Observable<(FundInvestment | null)[] | null>
  }
  capTableReportGenerate: (args: {
    asOfDate: Date
    email: String
    investmentId: UUID
  }) => CapTableReportGenerateObservableChain & {
    execute: (
      request: CapTableReportGenerateRequest,
      defaultValue?: CapTableReportGenerate | null,
    ) => Observable<CapTableReportGenerate | null>
  }
  capTableScenariosUpdate: (args: {
    scenarios: (CapTableScenarioItem | null)[]
  }) => {
    execute: (
      request: CapTableScenarioRequest,
      defaultValue?: (CapTableScenario | null)[] | null,
    ) => Observable<(CapTableScenario | null)[] | null>
  }
  fundScenariosAdd: (args: {
    scenarioSets: (FundScenariosAddSet | null)[]
  }) => {
    execute: (
      request: FundScenarioRequest,
      defaultValue?: (FundScenario | null)[] | null,
    ) => Observable<(FundScenario | null)[] | null>
  }
  fundScenariosChange: (args: {
    scenarios: (FundScenariosChangeItem | null)[]
  }) => {
    execute: (
      request: FundScenarioRequest,
      defaultValue?: (FundScenario | null)[] | null,
    ) => Observable<(FundScenario | null)[] | null>
  }
  /** Triggers datasync to calculate FundScenarios  */
  fundScenariosCalculate: (args: {
    fundId: UUID
    investmentId?: UUID | null
  }) => { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
  /** Deletes scenarios by list of IDs and returns amount of deleted rows.  */
  fundScenariosDelete: (args: {
    scenarios: (UUID | null)[]
  }) => { execute: (request?: boolean | number, defaultValue?: Int | null) => Observable<Int | null> }
  fundScenarioInvestmentScenariosAdd: (args: {
    scenarioSets: (FundScenarioInvestmentScenarioAddSet | null)[]
  }) => {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: (FundScenarioInvestmentScenario | null)[] | null,
    ) => Observable<(FundScenarioInvestmentScenario | null)[] | null>
  }
  fundScenarioInvestmentScenariosChange: (args: {
    scenarios: (FundScenarioInvestmentScenariosChangeItem | null)[]
  }) => {
    execute: (
      request: FundScenarioInvestmentScenarioRequest,
      defaultValue?: (FundScenarioInvestmentScenario | null)[] | null,
    ) => Observable<(FundScenarioInvestmentScenario | null)[] | null>
  }
  /** Deletes scenarios by list of IDs and returns amount of deleted rows.  */
  fundScenarioInvestmentScenariosDelete: (args: {
    scenarios: (UUID | null)[]
  }) => { execute: (request?: boolean | number, defaultValue?: Int | null) => Observable<Int | null> }
  companyDashboardSettingsUpdate: (args: {
    companyId: UUID
    dashboardSettings: SectionInput[]
  }) => DashboardSettingsUpdateObservableChain & {
    execute: (
      request: DashboardSettingsUpdateRequest,
      defaultValue?: DashboardSettingsUpdate | null,
    ) => Observable<DashboardSettingsUpdate | null>
  }
}

export interface LoginPromiseChain {
  session: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  error: { execute: (request?: boolean | number, defaultValue?: ErrorsEnumLogin | null) => Promise<ErrorsEnumLogin | null> }
}

export interface LoginObservableChain {
  session: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  error: {
    execute: (request?: boolean | number, defaultValue?: ErrorsEnumLogin | null) => Observable<ErrorsEnumLogin | null>
  }
}

export interface PasswordChangePromiseChain {
  session: { execute: (request?: boolean | number, defaultValue?: String | null) => Promise<String | null> }
  errorCode: {
    execute: (
      request?: boolean | number,
      defaultValue?: ErrorsEnumPasswordChange | null,
    ) => Promise<ErrorsEnumPasswordChange | null>
  }
  additionalMessages: {
    execute: (request?: boolean | number, defaultValue?: (String | null)[] | null) => Promise<(String | null)[] | null>
  }
}

export interface PasswordChangeObservableChain {
  session: { execute: (request?: boolean | number, defaultValue?: String | null) => Observable<String | null> }
  errorCode: {
    execute: (
      request?: boolean | number,
      defaultValue?: ErrorsEnumPasswordChange | null,
    ) => Observable<ErrorsEnumPasswordChange | null>
  }
  additionalMessages: {
    execute: (request?: boolean | number, defaultValue?: (String | null)[] | null) => Observable<(String | null)[] | null>
  }
}

export interface DashboardSettingsUpdatePromiseChain {
  dashboardSettings: SettingsPromiseChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Promise<Settings>
  }
}

export interface DashboardSettingsUpdateObservableChain {
  dashboardSettings: SettingsObservableChain & {
    execute: (request: SettingsRequest, defaultValue?: Settings) => Observable<Settings>
  }
}

export interface CapTableReportGeneratePromiseChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Promise<Boolean | null> }
}

export interface CapTableReportGenerateObservableChain {
  success: { execute: (request?: boolean | number, defaultValue?: Boolean | null) => Observable<Boolean | null> }
}
